import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import {
    Activity,
    ArrowLeft,
    Building2,
    ChevronDown,
    CircleCheck,
    ClipboardList,
    Edit,
    Key,
    KeyRound,
    Loader2,
    Mail,
    MessageSquare,
    Plus,
    Send,
    Settings,
    TestTube2,
    Trash2,
    Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';

import { A2pComplianceProfileModal } from '@apps/web/components/admin/a2p_compliance_profile_modal';
import { AddInvitedUserToOrgModal } from '@apps/web/components/admin/add_invited_user_to_org_modal';
import { CustomerAccountNotificationDestinations } from '@apps/web/components/admin/customer_account_notification_destinations';
import { DataSourceAccountModal } from '@apps/web/components/admin/data_source_account_modal';
import { EhrAccessCard } from '@apps/web/components/admin/ehr_access_card';
import { ExternalPhoneForwardingCard } from '@apps/web/components/admin/external_phone_forwarding_card';
import { ExternalSmsProviderAccountModal } from '@apps/web/components/admin/external_sms_provider_account_modal';
import { GenerationsPortalLoginsModal } from '@apps/web/components/admin/generations_portal_logins_modal';
import {
    CalloutSettingsJsonModal,
    GeneralSettingsJsonModal,
    ShiftConfirmationSettingsJsonModal,
} from '@apps/web/components/admin/json_settings_modal';
import { OnboardingReviewCard } from '@apps/web/components/admin/onboarding_review_card';
import { OrganizationChecklistPanel } from '@apps/web/components/admin/organization_checklist_panel';
import { OrganizationFormModal } from '@apps/web/components/admin/organization_form_modal';
import { OrganizationMemberEditModal } from '@apps/web/components/admin/organization_member_edit_modal';
import { PhoneNumberModal } from '@apps/web/components/admin/phone_number_modal';
import { PhoneNumberPortInPanel } from '@apps/web/components/admin/phone_number_port_in_panel';
import { QaVoiceAgentSettingsPanel } from '@apps/web/components/admin/qa_voice_agent_settings_panel';
import { SchedulerAssessmentsPanel } from '@apps/web/components/admin/scheduler_assessments_panel';
import { StaffImessageNotificationBudgetSection } from '@apps/web/components/admin/staff_imessage_notification_budget_section';
import { EntityEventsTimeline } from '@apps/web/components/events/entity_events_timeline';
import { CallRoutingWorkflowPreview } from '@apps/web/components/settings/call_routing_workflow_preview';
import { PhoneSystemForwardingCard } from '@apps/web/components/settings/phone_system_forwarding_card';
import { SettingsAuditTimeline } from '@apps/web/components/settings/settings_audit_timeline';
import { SlackChannelPicker } from '@apps/web/components/settings/slack_channel_picker';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card, CardContent } from '@apps/web/components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@apps/web/components/ui/sheet';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Switch } from '@apps/web/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { CAREGIVER_SIMULATOR_ENABLED_FEATURE_KEY } from '@apps/web/lib/caregiver_simulator';
import { isEhrCaregiverUnassignmentWritebackEnabled } from '@apps/web/lib/ehr_caregiver_unassignment_writeback';
import { orgMemberRoleLabel } from '@apps/web/lib/organization_permissions';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import {
    isInTrainingMode,
    trainingDayNumber,
} from '@apps/web/lib/training_mode';
import { cn } from '@apps/web/lib/utils';
import { CaregiverSmsAgentModeControl } from '@apps/web/routes/_app/admin/organizations/caregiver_sms_agent_mode_control';
import {
    buildDataSourceAccountModalInitialData,
    type DataSourceAccountModalInitialData,
} from '@apps/web/routes/_app/admin/organizations/data_source_account_modal_initial_data';
import { getCatalogFeatureFlags } from '@apps/web/routes/_app/admin/organizations/feature_flags';
import { GeneralSettingSwitchRow } from '@apps/web/routes/_app/admin/organizations/general_setting_switch_row';
import { buildBooleanGeneralSettingsBody } from '@apps/web/routes/_app/admin/organizations/general_settings_helpers';
import { buildOrganizationFormModalInitialData } from '@apps/web/routes/_app/admin/organizations/organization_form_modal_initial_data';
import { OrganizationInboundPayerRoutingCard } from '@apps/web/routes/_app/admin/organizations/organization_inbound_payer_routing_card';
import { OrganizationVoiceQualityModeCard } from '@apps/web/routes/_app/admin/organizations/organization_voice_quality_mode_card';
import { OrganizationVoiceSettingsCard } from '@apps/web/routes/_app/admin/organizations/organization_voice_settings_card';
import { OutreachSenderAllowlist } from '@apps/web/routes/_app/admin/organizations/outreach_sender_allowlist';
import {
    updateAdminOrganizationSubAccount,
    useAdminA2pComplianceProfile,
    useAdminOrganization,
    useAdminOrganizationCarePlanSettings,
    useAdminOrganizationDataSourceAccounts,
    useAdminOrganizationShiftConfirmationSettings,
    useAdminOrganizationSubAccount,
    useAdminTwilioA2pRegistration,
    useOrganizationExternalSmsProviderAccounts,
    useOrganizationInvitations,
    useOrganizationMembers,
    useOrganizationPhoneNumbers,
    useOrganizationVoiceSettings,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type A2pComplianceProfile,
    type AdminOrganization,
    ApiError,
    type ClientReferenceTerm,
    type DistanceCalculationMode,
    type Mode,
    type Organization,
    type OrgMemberProfile,
    type OrgMemberRole,
    type SmsProvider,
    type TextingCanaryAttempt,
    type TwilioA2pRegistration,
    type TwilioA2pRegistrationDetail,
    type TwilioA2pRegistrationUpdate,
} from '@libraries/typescript/api_client';

// Mirror of `SUPPORTED_SIDEBAR_OUTREACH_MODELS` in
// libraries/python/phoebe_agent_models/outreach.py. Keep in sync —
// the API rejects any value not on the Python allowlist, so a stale entry
// here would produce a 422 on save.
const SUPPORTED_SIDEBAR_OUTREACH_AGENT_MODELS: ReadonlyArray<string> = [
    'claude-opus-4-8',
    'claude-opus-4-6',
    'claude-sonnet-4-6',
    'claude-opus-4-5',
    'claude-sonnet-4-5',
    'gpt-5.5',
    'gpt-5.6-sol',
    'gpt-5.6-terra',
    'gpt-5.6-luna',
    'gpt-6-luna',
    'gemini-3.8-flash',
];
// Mirror of `SUPPORTED_SIDEBAR_OUTREACH_EFFORTS`.
const SUPPORTED_SIDEBAR_OUTREACH_AGENT_EFFORTS: ReadonlyArray<string> = [
    'low',
    'medium',
    'high',
];
const SIDEBAR_AGENT_MODEL_UNSET_VALUE = '__unset__';

// Mirrors MAX_SEED_COUNT_PER_ENTITY in
// services/api/routes/admin/orgs/organization_training_seed.py.
const MAX_SEED_COUNT_PER_ENTITY = 30;

function clampSeedCount(rawValue: string): number {
    const parsed = Number.parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
        return 0;
    }
    return Math.min(Math.max(parsed, 0), MAX_SEED_COUNT_PER_ENTITY);
}

function formatSeedCount(count: number, noun: string): string {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

type SeedMode = 'live' | 'sandbox';

function isSeedMode(value: string): value is SeedMode {
    return value === 'live' || value === 'sandbox';
}

type SmsInlineReviewMode = 'off' | 'preview' | 'enforce';
type SmsInlineReviewModeSelectValue = SmsInlineReviewMode | 'global';

const SMS_INLINE_REVIEW_MODE_OPTIONS: ReadonlyArray<{
    readonly value: SmsInlineReviewModeSelectValue;
    readonly label: string;
}> = [
    { value: 'global', label: 'Use Global' },
    { value: 'off', label: 'Off' },
    { value: 'preview', label: 'Preview' },
    { value: 'enforce', label: 'Enforce' },
];

function formatLifecycleStageLabel(
    lifecycleStage: Organization['customer_lifecycle_stage'],
): string | null {
    if (!lifecycleStage) {
        return null;
    }
    return lifecycleStage
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

const EHR_TYPE_LABELS: Record<string, string> = {
    axiscare: 'AxisCare',
    generations_homecare: 'Generations Homecare',
    wellsky: 'WellSky',
    hhaexchange: 'HHAeXchange',
    other: 'Other',
};

// organizations.voip_provider stores either a known provider identifier or
// the customer-typed provider name when they picked "Other" — fall back to
// showing the raw value for unrecognized strings.
const VOIP_PROVIDER_LABELS: Record<string, string> = {
    ringcentral: 'RingCentral',
    zoom_phone: 'Zoom Phone',
    goto: 'GoTo',
    dialpad: 'Dialpad',
    generic_forwarding: 'Forwarding / IVR only',
    other: 'Other',
};

// Mirrors MAX_CLOCK_GRACE_MINUTES in organization_settings_schemas — the
// inbound voice clock context only recognizes shifts up to an hour out.
const MAX_CLOCK_GRACE_MINUTES = 60;

// Mirror the MIN/MAX_CLOCK_REMINDER_MONITOR_WINDOW_HOURS bounds in
// organization_settings_schemas.
const MIN_CLOCK_REMINDER_MONITOR_WINDOW_HOURS = 1;
const MAX_CLOCK_REMINDER_MONITOR_WINDOW_HOURS = 24;

function formatEhrTypeLabel(ehrType: Organization['ehr_type']): string | null {
    if (!ehrType) {
        return null;
    }
    return EHR_TYPE_LABELS[ehrType] ?? ehrType;
}

function formatEhrConnectionLabel(
    ehrType: string | null | undefined,
): string | null {
    if (!ehrType) {
        return null;
    }
    return EHR_TYPE_LABELS[ehrType] ?? ehrType;
}

function formatVoipProviderLabel(
    voipProvider: string | null | undefined,
): string | null {
    if (!voipProvider) {
        return null;
    }
    return VOIP_PROVIDER_LABELS[voipProvider] ?? voipProvider;
}

function formatBusinessDate(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(new Date(`${value}T00:00:00Z`));
}

export const Route = createFileRoute(
    '/_app/admin/organizations/$organization-id',
)({
    head: () => ({
        meta: [
            {
                title: 'Organization Details | Phoebe Admin',
            },
        ],
    }),
    component: OrganizationDetailPage,
});

function OrganizationDetailPage() {
    const { 'organization-id': organizationId } = Route.useParams();
    const [activeTab, setActiveTab] = useState('settings');
    const { data: organization } = useAdminOrganization(organizationId);

    return (
        <div className="relative h-full w-full">
            <div className="absolute inset-0 overflow-auto p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/organizations"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Organizations
                    </Link>
                </div>
                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    <TabsList className="mb-6">
                        <TabsTrigger value="settings">Settings</TabsTrigger>
                        <TabsTrigger value="change-history">
                            Change history
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="settings">
                        <OrganizationDetails organizationId={organizationId} />
                    </TabsContent>
                    <TabsContent value="change-history">
                        <SettingsAuditTimeline
                            organizationId={organizationId}
                            timezone={organization?.timezone ?? 'UTC'}
                        />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

type FeatureFlagToggleProps = {
    readonly organization: Organization;
    readonly featureKey: string;
    readonly label: string;
    readonly description: string;
    readonly defaultEnabled?: boolean;
    readonly checkedOverride?: boolean;
    readonly onCheckedChangeOverride?: (checked: boolean) => Promise<void>;
};

type ClockSettingNumberInputProps = {
    readonly organization: Organization;
    readonly settingKey:
        | 'clock_in_grace_minutes'
        | 'clock_out_grace_minutes'
        | 'clock_reminder_monitor_window_hours';
    readonly label: string;
    readonly description: string;
    readonly min: number;
    readonly max: number;
};

/**
 * Number input for one integer clock reminder setting on
 * organization_general_settings (grace periods, EHR check window).
 */
function ClockSettingNumberInput({
    organization,
    settingKey,
    label,
    description,
    min,
    max,
}: ClockSettingNumberInputProps) {
    const { mutate: mutateOrganization } = useAdminOrganization(
        organization.id,
    );
    const savedValue = organization.general_settings?.[settingKey] ?? min;
    const [value, setValue] = useState(String(savedValue));
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setValue(String(savedValue));
    }, [savedValue]);

    const parsedValue = Number(value);
    const isValid =
        value.trim() !== '' &&
        Number.isInteger(parsedValue) &&
        parsedValue >= min &&
        parsedValue <= max;
    const isDirty = isValid && parsedValue !== savedValue;

    const handleSave = async () => {
        if (!organization.general_settings || !isValid) {
            return;
        }
        setIsSaving(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }
            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    [settingKey]: parsedValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: {
                    [settingKey]: parsedValue,
                },
            });

            toast.success(`${label} updated`, {
                persistNotification: false,
            });
        } catch {
            toast.error(`Failed to update ${label.toLowerCase()}`, {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-1">
            <Label htmlFor={settingKey} className="text-base font-medium">
                {label}
            </Label>
            <p className="text-muted-foreground text-sm">{description}</p>
            <div className="flex items-center gap-3 pt-1">
                <Input
                    id={settingKey}
                    type="number"
                    min={min}
                    max={max}
                    className="w-24"
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                />
                <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving || !isDirty}
                >
                    {isSaving ? (
                        <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Saving…
                        </span>
                    ) : (
                        'Save'
                    )}
                </Button>
            </div>
        </div>
    );
}

function FeatureFlagToggle({
    organization,
    featureKey,
    label,
    description,
    defaultEnabled,
    checkedOverride,
    onCheckedChangeOverride,
}: FeatureFlagToggleProps) {
    const { mutate: mutateOrganization } = useAdminOrganization(
        organization.id,
    );
    const rawValue = organization.features?.[featureKey];
    // The catalog owns whether an unset flag means on or off, so this page and
    // the bulk Feature Flags tab cannot disagree. The prop stays as an
    // override for call sites that predate the catalog attribute.
    const resolvedDefaultEnabled =
        defaultEnabled ??
        getCatalogFeatureFlags().find((flag) => flag.key === featureKey)
            ?.defaultEnabled ??
        false;
    const checked =
        checkedOverride ??
        (typeof rawValue === 'boolean' ? rawValue : resolvedDefaultEnabled);

    return (
        <div className="flex items-center justify-between">
            <div className="space-y-0.5">
                <Label htmlFor={featureKey} className="text-base font-medium">
                    {label}
                </Label>
                <p className="text-muted-foreground text-sm">{description}</p>
            </div>
            <Switch
                id={featureKey}
                checked={checked}
                onCheckedChange={async (checked) => {
                    if (onCheckedChangeOverride) {
                        await onCheckedChangeOverride(checked);
                        return;
                    }

                    const updatedFeatures = {
                        ...organization.features,
                        [featureKey]: checked,
                    };

                    await mutateOrganization((currentData) => {
                        if (!currentData) {
                            return currentData;
                        }
                        return {
                            ...currentData,
                            features: updatedFeatures,
                        };
                    }, false);

                    try {
                        await getApiClient().patch({
                            url: '/admin/organizations/{organization_id}/feature-flags',
                            pathParams: {
                                organization_id: organization.id,
                            },
                            body: {
                                features: { [featureKey]: checked },
                            },
                        });

                        toast.success('Feature flag updated successfully', {
                            persistNotification: false,
                        });
                        await mutateOrganization();
                    } catch (error) {
                        toast.error('Failed to update feature flag', {
                            persistNotification: false,
                        });
                        await mutateOrganization();
                    }
                }}
            />
        </div>
    );
}

function SmsShiftCancellationSettingToggle({
    organization,
}: {
    readonly organization: Organization;
}) {
    const { mutate: mutateOrganization } = useAdminOrganization(
        organization.id,
    );
    const [isUpdating, setIsUpdating] = useState(false);
    const checked =
        organization.general_settings?.sms_shift_cancellation_user_enabled ===
        true;

    const handleCheckedChange = async (nextValue: boolean) => {
        setIsUpdating(true);
        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: buildBooleanGeneralSettingsBody(
                    'sms_shift_cancellation_user_enabled',
                    nextValue,
                ),
            });
            toast.success('SMS shift cancellation setting updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update SMS shift cancellation setting', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdating(false);
        }
    };

    return (
        <div className="flex items-center justify-between">
            <div className="space-y-0.5">
                <Label
                    htmlFor="sms-shift-cancellation-setting"
                    className="text-base font-medium"
                >
                    SMS Shift Cancellations
                </Label>
                <p className="text-muted-foreground text-sm">
                    Allow caregivers to cancel eligible assigned shifts by text.
                    This is the same org-admin setting shown in Settings; it
                    defaults off.
                </p>
            </div>
            <Switch
                id="sms-shift-cancellation-setting"
                checked={checked}
                disabled={isUpdating}
                onCheckedChange={handleCheckedChange}
            />
        </div>
    );
}

function normalizeSmsInlineReviewMode(
    value: unknown,
): SmsInlineReviewModeSelectValue {
    if (value === 'off' || value === 'preview' || value === 'enforce') {
        return value;
    }
    return 'global';
}

function SmsInlineReviewModeSelect({
    organization,
}: {
    readonly organization: Organization;
}) {
    const { mutate: mutateOrganization } = useAdminOrganization(
        organization.id,
    );
    const selectedMode = normalizeSmsInlineReviewMode(
        organization.features?.sms_inline_review_mode,
    );

    return (
        <div className="flex items-center justify-between">
            <div className="space-y-0.5">
                <Label
                    htmlFor="sms_inline_review_mode"
                    className="text-base font-medium"
                >
                    Inline SMS Review Mode
                </Label>
                <p className="text-muted-foreground text-sm">
                    Override inline SMS review behavior for this organization.
                </p>
            </div>
            <Select
                value={selectedMode}
                onValueChange={async (value) => {
                    const nextMode = value as SmsInlineReviewModeSelectValue;
                    const updatedFeatures = { ...organization.features };
                    if (nextMode === 'global') {
                        delete updatedFeatures.sms_inline_review_mode;
                    } else {
                        updatedFeatures.sms_inline_review_mode = nextMode;
                    }

                    await mutateOrganization((currentData) => {
                        if (!currentData) {
                            return currentData;
                        }
                        return {
                            ...currentData,
                            features: updatedFeatures,
                        };
                    }, false);

                    try {
                        await getApiClient().put({
                            url: '/admin/organizations/{organization_id}',
                            pathParams: {
                                organization_id: organization.id,
                            },
                            body: {
                                features: updatedFeatures,
                            },
                        });

                        toast.success('Inline SMS review mode updated', {
                            persistNotification: false,
                        });
                        await mutateOrganization();
                    } catch {
                        toast.error('Failed to update inline SMS review mode', {
                            persistNotification: false,
                        });
                        await mutateOrganization();
                    }
                }}
            >
                <SelectTrigger
                    id="sms_inline_review_mode"
                    className="w-[160px]"
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {SMS_INLINE_REVIEW_MODE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

type ActivityFeedNotePriority = 'normal' | 'high' | 'urgent';

type EditableActivityFeedKindFlag =
    | 'callout_opened_enabled'
    | 'callout_closed_enabled'
    | 'callout_response_enabled'
    | 'outreach_status_update_enabled'
    | 'outreach_action_needed_enabled'
    | 'missed_clock_in_enabled'
    | 'missed_clock_out_enabled'
    | 'shift_cancellation_enabled'
    | 'after_hours_receptionist_enabled';
type ActivityFeedKindFlag =
    | EditableActivityFeedKindFlag
    | 'sms_agency_escalation_enabled';

const ACTIVITY_FEED_NOTE_PRIORITIES: ReadonlyArray<{
    readonly value: ActivityFeedNotePriority;
    readonly label: string;
}> = [
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
];

type ActivityFeedKindToggle =
    | {
          readonly key: EditableActivityFeedKindFlag;
          readonly label: string;
          readonly description: string;
          readonly lockedOn?: false;
      }
    | {
          readonly key: 'sms_agency_escalation_enabled';
          readonly label: string;
          readonly description: string;
          readonly lockedOn: true;
      };

const ACTIVITY_FEED_KIND_TOGGLES: ReadonlyArray<ActivityFeedKindToggle> = [
    {
        key: 'callout_opened_enabled',
        label: 'Open Shift Opened Events',
        description: 'Emit an activity event when a callout is opened.',
    },
    {
        key: 'callout_closed_enabled',
        label: 'Open Shift Closed Events',
        description: 'Emit an activity event when a callout is closed.',
    },
    {
        key: 'callout_response_enabled',
        label: 'Open Shift Response Events',
        description:
            'Emit an activity event when a caregiver responds to a callout.',
    },
    {
        key: 'outreach_status_update_enabled',
        label: 'Open Shift Availability Update Events',
        description:
            'Emit an activity event when a caregiver shares availability or work preferences during open-shift outreach.',
    },
    {
        key: 'outreach_action_needed_enabled',
        label: 'Open Shift Action Needed Events',
        description:
            "Emit activity events when a caregiver's response needs review or an assignment requires manual confirmation.",
    },
    {
        key: 'missed_clock_in_enabled',
        label: 'Missed Clock-In Events',
        description:
            'Emit an activity event when a caregiver misses a clock-in.',
    },
    {
        key: 'missed_clock_out_enabled',
        label: 'Missed Clock-Out Events',
        description:
            'Emit an activity event when a caregiver misses a clock-out.',
    },
    {
        key: 'shift_cancellation_enabled',
        label: 'Shift Cancellation Events',
        description:
            'Emit an activity event when a caregiver cancels an assigned shift.',
    },
    {
        key: 'after_hours_receptionist_enabled',
        label: 'Call Summary Events',
        description: 'Emit an activity event when Phoebe captures a call.',
    },
    {
        key: 'sms_agency_escalation_enabled',
        label: 'SMS Agency Escalation Events',
        description:
            'Emit an activity event when a caregiver asks Phoebe to pass information to the office.',
        lockedOn: true,
    },
];

function ActivityFeedSettingsSection({
    organization,
}: {
    readonly organization: Organization;
}) {
    const { mutate: mutateOrganization } = useAdminOrganization(
        organization.id,
    );
    const features = organization.features ?? {};
    const settings =
        (features['activity_feed_settings'] as
            | {
                  voice_call_note_emit_min_priority?: ActivityFeedNotePriority | null;
                  voice_call_note_emit_only_action_required?: boolean | null;
                  callout_opened_enabled?: boolean | null;
                  callout_closed_enabled?: boolean | null;
                  callout_response_enabled?: boolean | null;
                  outreach_status_update_enabled?: boolean | null;
                  outreach_action_needed_enabled?: boolean | null;
                  missed_clock_in_enabled?: boolean | null;
                  missed_clock_out_enabled?: boolean | null;
                  shift_cancellation_enabled?: boolean | null;
                  sms_agency_escalation_enabled?: boolean | null;
                  after_hours_receptionist_enabled?: boolean | null;
              }
            | null
            | undefined) ?? null;
    // Backend default for the priority floor is `high` (see
    // libraries/python/activity_events/emitter.py
    // _DEFAULT_VOICE_CALL_NOTE_MIN_PRIORITY). Match it here so the radio
    // selection reflects the actual emit behavior when no override is
    // set.
    const minPriority: ActivityFeedNotePriority =
        settings?.voice_call_note_emit_min_priority ?? 'high';
    const onlyActionRequired = Boolean(
        settings?.voice_call_note_emit_only_action_required,
    );

    function kindEnabled(name: ActivityFeedKindFlag): boolean {
        const raw = settings?.[name];
        // Mirror backend defaults for unset kind flags — see
        // libraries/python/activity_events/emitter.py:_kind_enabled_for_settings.
        if (raw !== undefined && raw !== null) {
            return Boolean(raw);
        }
        return (
            name === 'shift_cancellation_enabled' ||
            name === 'after_hours_receptionist_enabled' ||
            name === 'sms_agency_escalation_enabled'
        );
    }

    async function persistFeatures(
        updater: (current: Record<string, unknown>) => Record<string, unknown>,
        successMessage: string,
    ) {
        const updatedFeatures = updater({ ...features });
        await mutateOrganization((currentData) => {
            if (!currentData) {
                return currentData;
            }
            return { ...currentData, features: updatedFeatures };
        }, false);
        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}',
                pathParams: { organization_id: organization.id },
                body: { features: updatedFeatures },
            });
            toast.success(successMessage, { persistNotification: false });
            await mutateOrganization();
        } catch {
            toast.error('Failed to update activity feed settings', {
                persistNotification: false,
            });
            await mutateOrganization();
        }
    }

    function updateActivityFeedSettings(
        next: Partial<{
            readonly voice_call_note_emit_min_priority: ActivityFeedNotePriority;
            readonly voice_call_note_emit_only_action_required: boolean;
            readonly callout_opened_enabled: boolean;
            readonly callout_closed_enabled: boolean;
            readonly callout_response_enabled: boolean;
            readonly outreach_status_update_enabled: boolean;
            readonly outreach_action_needed_enabled: boolean;
            readonly missed_clock_in_enabled: boolean;
            readonly missed_clock_out_enabled: boolean;
            readonly shift_cancellation_enabled: boolean;
        }>,
    ) {
        return persistFeatures(
            (current) => ({
                ...current,
                activity_feed_settings: {
                    ...(typeof current['activity_feed_settings'] === 'object' &&
                    current['activity_feed_settings'] !== null
                        ? (current['activity_feed_settings'] as Record<
                              string,
                              unknown
                          >)
                        : {}),
                    ...next,
                },
            }),
            'Activity feed settings updated',
        );
    }

    return (
        <div className="border-border bg-muted/50 space-y-4 rounded-md border p-3">
            <h3 className="text-base font-semibold">Activity Feed</h3>
            {ACTIVITY_FEED_KIND_TOGGLES.map((toggle) => (
                <div
                    key={toggle.key}
                    className="flex items-center justify-between"
                >
                    <div className="space-y-0.5">
                        <Label
                            htmlFor={toggle.key}
                            className="text-base font-medium"
                        >
                            {toggle.label}
                        </Label>
                        <p className="text-muted-foreground text-sm">
                            {toggle.description}
                        </p>
                    </div>
                    <Switch
                        id={toggle.key}
                        checked={
                            toggle.lockedOn ? true : kindEnabled(toggle.key)
                        }
                        disabled={toggle.lockedOn}
                        onCheckedChange={(checked) =>
                            toggle.lockedOn
                                ? undefined
                                : void updateActivityFeedSettings({
                                      [toggle.key]: checked,
                                  })
                        }
                    />
                </div>
            ))}
            <div className="space-y-2">
                <Label className="text-base font-medium">
                    Voice Call Note Priority Floor
                </Label>
                <p className="text-muted-foreground text-sm">
                    Minimum priority required for non-action-required voice call
                    notes to be emitted to the feed.
                </p>
                <div className="flex flex-wrap gap-3">
                    {ACTIVITY_FEED_NOTE_PRIORITIES.map((option) => (
                        <label
                            key={option.value}
                            className="flex items-center gap-2 text-sm"
                        >
                            <input
                                type="radio"
                                name="voice_call_note_emit_min_priority"
                                value={option.value}
                                checked={minPriority === option.value}
                                onChange={() =>
                                    void updateActivityFeedSettings({
                                        voice_call_note_emit_min_priority:
                                            option.value,
                                    })
                                }
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            </div>
            <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                    <Label
                        htmlFor="voice_call_note_emit_only_action_required"
                        className="text-base font-medium"
                    >
                        Only Emit Action-Required Notes
                    </Label>
                    <p className="text-muted-foreground text-sm">
                        When enabled, only voice call notes flagged as action
                        required are emitted to the feed.
                    </p>
                </div>
                <Switch
                    id="voice_call_note_emit_only_action_required"
                    checked={onlyActionRequired}
                    onCheckedChange={(checked) =>
                        void updateActivityFeedSettings({
                            voice_call_note_emit_only_action_required: checked,
                        })
                    }
                />
            </div>
        </div>
    );
}

function InternalDailyDigestSlackChannelSection({
    organization,
}: {
    readonly organization: AdminOrganization;
}) {
    const { mutate: mutateOrganization } = useAdminOrganization(
        organization.id,
    );
    const savedChannelId = organization.internal_slack_channel_id ?? '';
    const [draftChannelId, setDraftChannelId] = useState(savedChannelId);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setDraftChannelId(savedChannelId);
    }, [savedChannelId]);

    const hasChanges = draftChannelId.trim() !== savedChannelId;

    async function saveChannel() {
        setIsSaving(true);
        await mutateOrganization((currentData) => {
            if (!currentData) {
                return currentData;
            }
            return {
                ...currentData,
                internal_slack_channel_id: draftChannelId.trim() || null,
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}',
                pathParams: {
                    organization_id: organization.id,
                },
                body: {
                    internal_slack_channel_id: draftChannelId.trim() || null,
                },
            });
            toast.success('Internal digest channel updated', {
                persistNotification: false,
            });
            await mutateOrganization();
        } catch {
            toast.error('Failed to update internal digest channel', {
                persistNotification: false,
            });
            await mutateOrganization();
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Card className="p-6">
            <div className="mb-4 flex items-center gap-2">
                <MessageSquare className="text-muted-foreground/70 h-5 w-5" />
                <h2 className="text-lg font-semibold">Internal Daily Digest</h2>
            </div>
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                <div className="space-y-2">
                    <Label
                        htmlFor="internal-daily-digest-slack-channel"
                        className="text-sm font-medium"
                    >
                        Slack channel
                    </Label>
                    <SlackChannelPicker
                        allowClear={true}
                        ariaLabel="Internal daily digest Slack channel"
                        disabled={isSaving}
                        showMembershipHints={false}
                        organizationId={organization.id}
                        placeholder="Search internal #channel"
                        searchPath="/admin/organizations/{organization_id}/internal-slack/channels"
                        triggerId="internal-daily-digest-slack-channel"
                        value={draftChannelId}
                        onChange={setDraftChannelId}
                    />
                </div>
                <Button
                    type="button"
                    size="sm"
                    disabled={!hasChanges || isSaving}
                    onClick={() => void saveChannel()}
                >
                    {isSaving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Save
                </Button>
            </div>
        </Card>
    );
}

function OrganizationDetails({ organizationId }: { organizationId: string }) {
    const { user } = Route.useRouteContext();
    const router = useRouter();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isActivitySheetOpen, setIsActivitySheetOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSeeding, setIsSeeding] = useState(false);
    const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
    const { mutate } = useSWRConfig();

    const {
        data: organization,
        isLoading: isLoadingOrganization,
        mutate: mutateOrganization,
    } = useAdminOrganization(organizationId);
    const {
        data: subAccount,
        isLoading: isLoadingSubAccount,
        error: subAccountError,
        mutate: mutateSubAccount,
    } = useAdminOrganizationSubAccount(organizationId);
    const [isUpdatingSubAccount, setIsUpdatingSubAccount] = useState(false);
    const [isUpdatingTrainingMode, setIsUpdatingTrainingMode] = useState(false);
    const [isRestartDialogOpen, setIsRestartDialogOpen] = useState(false);
    const [wipeConfirmText, setWipeConfirmText] = useState('');
    const [isSeedDialogOpen, setIsSeedDialogOpen] = useState(false);
    const [isSeedingTrainingData, setIsSeedingTrainingData] = useState(false);
    const [seedMode, setSeedMode] = useState<SeedMode>('live');
    const [seedCaregiverCount, setSeedCaregiverCount] = useState(0);
    const [seedClientCount, setSeedClientCount] = useState(0);
    const [seedShiftCount, setSeedShiftCount] = useState(0);
    const {
        data: a2pComplianceProfile,
        isLoading: isLoadingA2pComplianceProfile,
        mutate: mutateA2pComplianceProfile,
    } = useAdminA2pComplianceProfile(organizationId);
    const [isSavingA2p, setIsSavingA2p] = useState(false);
    const [isDeletingA2p, setIsDeletingA2p] = useState(false);
    const [isRegeneratingA2p, setIsRegeneratingA2p] = useState(false);
    const [isA2pModalOpen, setIsA2pModalOpen] = useState(false);
    const {
        data: twilioA2pRegistration,
        isLoading: isLoadingTwilioA2pRegistration,
        mutate: mutateTwilioA2pRegistration,
    } = useAdminTwilioA2pRegistration(organizationId);
    const [isSavingTwilioA2p, setIsSavingTwilioA2p] = useState(false);
    const [isStartingTwilioA2p, setIsStartingTwilioA2p] = useState(false);
    const [isPurgingTwilioA2p, setIsPurgingTwilioA2p] = useState(false);
    const [isSendingTestText, setIsSendingTestText] = useState(false);
    const [isShiftJsonModalOpen, setIsShiftJsonModalOpen] = useState(false);
    const [isCalloutJsonModalOpen, setIsCalloutJsonModalOpen] = useState(false);
    const [isGeneralJsonModalOpen, setIsGeneralJsonModalOpen] = useState(false);
    const [isUpdatingCaregiverNameFormat, setIsUpdatingCaregiverNameFormat] =
        useState(false);
    const [isUpdatingSmsBodyInEmail, setIsUpdatingSmsBodyInEmail] =
        useState(false);
    const [isUpdatingCallRecordingNotice, setIsUpdatingCallRecordingNotice] =
        useState(false);
    const [
        isUpdatingRequireWellSkyClientRate,
        setIsUpdatingRequireWellSkyClientRate,
    ] = useState(false);
    const [
        isUpdatingRequireWellSkyCaregiverRate,
        setIsUpdatingRequireWellSkyCaregiverRate,
    ] = useState(false);
    const [
        isUpdatingAxisCareServiceRateSelection,
        setIsUpdatingAxisCareServiceRateSelection,
    ] = useState(false);
    const [isUpdatingClientReferenceTerm, setIsUpdatingClientReferenceTerm] =
        useState(false);
    const [
        isUpdatingDistanceCalculationMode,
        setIsUpdatingDistanceCalculationMode,
    ] = useState(false);
    const [
        isUpdatingSidebarOutreachAgentModel,
        setIsUpdatingSidebarOutreachAgentModel,
    ] = useState(false);
    const [
        isUpdatingSidebarOutreachAgentEffort,
        setIsUpdatingSidebarOutreachAgentEffort,
    ] = useState(false);
    const [assistantName, setAssistantName] = useState('');
    const [initialAssistantName, setInitialAssistantName] = useState('');
    const [isSavingAssistantName, setIsSavingAssistantName] = useState(false);
    const [removingMemberId, setRemovingMemberId] = useState<string | null>(
        null,
    );
    const [editingMember, setEditingMember] = useState<OrgMemberProfile | null>(
        null,
    );
    const [deletingInvitationId, setDeletingInvitationId] = useState<
        string | null
    >(null);
    const [addToOrgInvitation, setAddToOrgInvitation] = useState<{
        email: string;
        first_name: string | null;
        last_name: string | null;
        role: OrgMemberRole | null;
    } | null>(null);
    const [editingSmsAccount, setEditingSmsAccount] = useState<{
        id: string;
        data: {
            provider: SmsProvider;
            displayName: string;
            isActive: boolean;
        };
    } | null>(null);
    const [isDataSourceAccountModalOpen, setIsDataSourceAccountModalOpen] =
        useState(false);
    const [editingDataSourceAccount, setEditingDataSourceAccount] = useState<{
        id: string;
        data: DataSourceAccountModalInitialData;
    } | null>(null);
    const [
        generationsPortalLoginsAccountId,
        setGenerationsPortalLoginsAccountId,
    ] = useState<string | null>(null);
    const [isSmsNumberModalOpen, setIsSmsNumberModalOpen] = useState(false);
    const [editingPhoneNumber, setEditingPhoneNumber] = useState<{
        id: string;
        data: {
            sms_provider: SmsProvider | null;
            number: string;
            messaging_service_sid?: string | undefined;
            external_sms_account_id?: string | undefined;
            mode: Mode;
            sms_enabled: boolean;
            voice_enabled: boolean;
            display_name?: string | undefined;
            role: 'main' | 'direct';
            default_call_routing_destination_id?: string | undefined;
        };
    } | null>(null);
    const canRemoveMembers = user?.admin ?? false;
    const phoneSystemMultiLineEnabled =
        organization?.features?.phone_system_multi_line_enabled === true;
    const phoneSystemPortInEnabled =
        organization?.features?.['phone_system_port_in_enabled'] === true;
    const phoneSystemE911Enabled =
        organization?.features?.phone_system_e911_enabled === true;

    const {
        data: membersResponse,
        isLoading: isLoadingMembers,
        mutate: mutateMembers,
    } = useOrganizationMembers(organizationId);
    const members = membersResponse?.members;
    const {
        data: invitationsResponse,
        isLoading: isLoadingInvitations,
        mutate: mutateInvitations,
    } = useOrganizationInvitations(organizationId);
    const invitations = invitationsResponse?.invitations;
    const {
        data: externalSmsProviderAccounts,
        isLoading: isLoadingExternalSmsProviderAccounts,
        mutate: mutateExternalSmsProviderAccounts,
    } = useOrganizationExternalSmsProviderAccounts(organizationId);
    const {
        data: dataSourceAccounts,
        isLoading: isLoadingDataSourceAccounts,
        mutate: mutateDataSourceAccounts,
    } = useAdminOrganizationDataSourceAccounts(organizationId);
    const {
        data: phoneNumbers,
        isLoading: isLoadingPhoneNumbers,
        mutate: mutatePhoneNumbers,
    } = useOrganizationPhoneNumbers(organizationId);
    const {
        data: shiftConfirmationSettings,
        mutate: mutateShiftConfirmationSettings,
    } = useAdminOrganizationShiftConfirmationSettings(organizationId);
    const {
        data: voiceSettings,
        isLoading: isLoadingVoiceSettings,
        mutate: mutateVoiceSettings,
    } = useOrganizationVoiceSettings(organizationId);
    const { data: carePlanSettings, mutate: mutateCarePlanSettings } =
        useAdminOrganizationCarePlanSettings(organizationId);

    useEffect(() => {
        const nextAssistantName =
            organization?.general_settings?.assistant_name ?? '';
        setAssistantName(nextAssistantName);
        setInitialAssistantName(nextAssistantName);
    }, [organization?.general_settings?.assistant_name]);

    const [editingEssentialItems, setEditingEssentialItems] =
        useState<Array<string> | null>(null);
    const [editingCustomInstructions, setEditingCustomInstructions] = useState<
        string | null
    >(null);
    const [isSavingCarePlanSettings, setIsSavingCarePlanSettings] =
        useState(false);

    const handleSaveCarePlanSettings = async () => {
        if (!organizationId) {
            return;
        }

        setIsSavingCarePlanSettings(true);
        try {
            const api = getApiClient();
            await api.put({
                url: '/admin/organizations/{organization_id}/care-plan-settings',
                pathParams: { organization_id: organizationId },
                body: {
                    ...(editingEssentialItems !== null && {
                        essential_items: editingEssentialItems,
                    }),
                    ...(editingCustomInstructions !== null && {
                        custom_instructions: editingCustomInstructions || null,
                    }),
                },
            });
            await mutateCarePlanSettings();
            setEditingEssentialItems(null);
            setEditingCustomInstructions(null);
            toast.success('Care plan settings saved', {
                persistNotification: false,
            });
        } catch (error) {
            console.error('Failed to save care plan settings:', error);
            toast.error('Failed to save care plan settings', {
                persistNotification: false,
            });
        } finally {
            setIsSavingCarePlanSettings(false);
        }
    };

    const handleToggleSubAccount = async (nextValue: boolean) => {
        setIsUpdatingSubAccount(true);

        await mutateSubAccount(
            (current) =>
                current
                    ? {
                          ...current,
                          is_sub_account: nextValue,
                      }
                    : current,
            false,
        );

        try {
            await updateAdminOrganizationSubAccount(organizationId, {
                is_sub_account: nextValue,
            });
            await mutateSubAccount();
            toast.success('Sub account status updated', {
                persistNotification: false,
            });
        } catch (error) {
            await mutateSubAccount();
            toast.error('Failed to update sub account status', {
                persistNotification: false,
            });
        } finally {
            setIsUpdatingSubAccount(false);
        }
    };

    const handleToggleTrainingMode = async (nextValue: boolean) => {
        setIsUpdatingTrainingMode(true);
        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}',
                pathParams: { organization_id: organizationId },
                body: { training_mode_enabled: nextValue },
            });
            await mutateOrganization();
            toast.success(
                nextValue ? 'Training mode enabled' : 'Training mode disabled',
                {
                    persistNotification: false,
                },
            );
        } catch {
            toast.error('Failed to update training mode', {
                persistNotification: false,
            });
        } finally {
            setIsUpdatingTrainingMode(false);
        }
    };

    // Only internal Phoebe / simulator orgs may wipe learned training data;
    // must mirror the reset endpoint's is_internal_or_simulator_org guard.
    const isInternalOrg =
        Boolean(organization?.is_internal_phoebe) ||
        organization?.features?.[CAREGIVER_SIMULATOR_ENABLED_FEATURE_KEY] ===
            true;
    // Seeding and reset/wipe both mutate the same caregiver/client/shift
    // rows, so gate every trigger on both busy flags — otherwise dismissing
    // one dialog mid-request (e.g. Escape) leaves the other action clickable
    // and races a bulk insert against a bulk delete.
    const isTrainingModeActionBusy =
        isSeedingTrainingData || isUpdatingTrainingMode;
    const restartTrainingWindow = async () => {
        setIsUpdatingTrainingMode(true);
        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}',
                pathParams: { organization_id: organizationId },
                body: { training_mode_restart: true },
            });
            await mutateOrganization();
            setIsRestartDialogOpen(false);
            toast.success('Training window restarted', {
                persistNotification: false,
            });
        } catch {
            toast.error('Failed to restart training mode', {
                persistNotification: false,
            });
        } finally {
            setIsUpdatingTrainingMode(false);
        }
    };
    const wipeAndRestartTraining = async () => {
        setIsUpdatingTrainingMode(true);
        try {
            await getApiClient().post({
                url: '/admin/organizations/{organization_id}/training/reset',
                pathParams: { organization_id: organizationId },
            });
            await mutateOrganization();
            setIsRestartDialogOpen(false);
            toast.success('Training data wiped and window restarted', {
                persistNotification: false,
            });
        } catch {
            toast.error('Failed to wipe training data', {
                persistNotification: false,
            });
        } finally {
            setIsUpdatingTrainingMode(false);
        }
    };
    const handleRestartTrainingMode = () => {
        // Internal/QA orgs get the two-option dialog (restart vs wipe). A real
        // customer org only ever gets a plain window restart — never a wipe.
        if (isInternalOrg) {
            setWipeConfirmText('');
            setIsRestartDialogOpen(true);
            return;
        }
        const confirmed = window.confirm(
            'Restart the 21-day training window? The start date resets and ' +
                'a fresh checklist window begins; the current window is ' +
                'kept as history.',
        );
        if (confirmed) {
            void restartTrainingWindow();
        }
    };

    const seedTrainingModeData = async () => {
        setIsSeedingTrainingData(true);
        try {
            const result = await getApiClient().post({
                url: '/admin/organizations/{organization_id}/training/seed',
                pathParams: { organization_id: organizationId },
                body: {
                    mode: seedMode,
                    caregiver_count: seedCaregiverCount,
                    client_count: seedClientCount,
                    shift_count: seedShiftCount,
                },
            });
            toast.success(
                `Seeded ${formatSeedCount(result.caregivers_created, 'caregiver')}, ` +
                    `${formatSeedCount(result.clients_created, 'client')}, ` +
                    `${formatSeedCount(result.shifts_created, 'shift')}`,
                { persistNotification: false },
            );
            setIsSeedDialogOpen(false);
            setSeedCaregiverCount(0);
            setSeedClientCount(0);
            setSeedShiftCount(0);
        } catch {
            toast.error('Failed to seed test data', {
                persistNotification: false,
            });
        } finally {
            setIsSeedingTrainingData(false);
        }
    };

    const handleToggleCaregiverNameFormat = async (nextValue: boolean) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingCaregiverNameFormat(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    display_caregiver_names_last_first: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: buildBooleanGeneralSettingsBody(
                    'display_caregiver_names_last_first',
                    nextValue,
                ),
            });

            toast.success('Caregiver name display updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update caregiver name display', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingCaregiverNameFormat(false);
        }
    };

    const handleSaveAssistantName = async () => {
        if (!organization?.general_settings) {
            return;
        }

        const nextAssistantName = assistantName.trim() || null;
        setIsSavingAssistantName(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    assistant_name: nextAssistantName,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: {
                    assistant_name: nextAssistantName,
                },
            });

            setInitialAssistantName(nextAssistantName ?? '');
            toast.success('Assistant name updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update assistant name', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsSavingAssistantName(false);
        }
    };

    const handleToggleSmsBodyInEmail = async (nextValue: boolean) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingSmsBodyInEmail(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    include_sms_body_in_email_notification: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: buildBooleanGeneralSettingsBody(
                    'include_sms_body_in_email_notification',
                    nextValue,
                ),
            });

            toast.success('SMS body in email setting updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update SMS body in email setting', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingSmsBodyInEmail(false);
        }
    };

    const handleToggleCallRecordingNotice = async (nextValue: boolean) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingCallRecordingNotice(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    call_recording_notice_enabled: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: buildBooleanGeneralSettingsBody(
                    'call_recording_notice_enabled',
                    nextValue,
                ),
            });

            toast.success('Call recording notice updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update call recording notice', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingCallRecordingNotice(false);
        }
    };

    const handleToggleRequireWellSkyClientRate = async (nextValue: boolean) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingRequireWellSkyClientRate(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    require_wellsky_client_rate_to_fill: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: buildBooleanGeneralSettingsBody(
                    'require_wellsky_client_rate_to_fill',
                    nextValue,
                ),
            });

            toast.success('WellSky client rate requirement updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update WellSky client rate requirement', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingRequireWellSkyClientRate(false);
        }
    };

    const handleToggleRequireWellSkyCaregiverRate = async (
        nextValue: boolean,
    ) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingRequireWellSkyCaregiverRate(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    require_wellsky_caregiver_rate_to_fill: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: buildBooleanGeneralSettingsBody(
                    'require_wellsky_caregiver_rate_to_fill',
                    nextValue,
                ),
            });

            toast.success('WellSky caregiver rate requirement updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update WellSky caregiver rate requirement', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingRequireWellSkyCaregiverRate(false);
        }
    };

    const handleToggleAxisCareServiceRateSelection = async (
        nextValue: boolean,
    ) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingAxisCareServiceRateSelection(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    axiscare_service_rate_selection_enabled: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: buildBooleanGeneralSettingsBody(
                    'axiscare_service_rate_selection_enabled',
                    nextValue,
                ),
            });

            toast.success('AxisCare service rate selection updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update AxisCare service rate selection', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingAxisCareServiceRateSelection(false);
        }
    };

    const handleUpdateSidebarOutreachAgentModel = async (
        nextValue: string | null,
    ) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingSidebarOutreachAgentModel(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    sidebar_outreach_agent_model: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: {
                    sidebar_outreach_agent_model: nextValue,
                },
            });

            toast.success('Sidebar agent model updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update sidebar agent model', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingSidebarOutreachAgentModel(false);
        }
    };

    const handleUpdateSidebarOutreachAgentEffort = async (
        nextValue: string | null,
    ) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingSidebarOutreachAgentEffort(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    sidebar_outreach_agent_effort: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: {
                    sidebar_outreach_agent_effort: nextValue,
                },
            });

            toast.success('Sidebar agent effort updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update sidebar agent effort', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingSidebarOutreachAgentEffort(false);
        }
    };

    const handleUpdateClientReferenceTerm = async (
        nextValue: ClientReferenceTerm,
    ) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingClientReferenceTerm(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    client_reference_term: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: {
                    client_reference_term: nextValue,
                },
            });

            toast.success('Client reference term updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update client reference term', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingClientReferenceTerm(false);
        }
    };

    const handleUpdateDistanceCalculationMode = async (
        nextValue: DistanceCalculationMode,
    ) => {
        if (!organization?.general_settings) {
            return;
        }

        setIsUpdatingDistanceCalculationMode(true);

        await mutateOrganization((current) => {
            if (!current?.general_settings) {
                return current;
            }

            return {
                ...current,
                general_settings: {
                    ...current.general_settings,
                    distance_calculation_mode: nextValue,
                },
            };
        }, false);

        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}/general-settings',
                pathParams: {
                    organization_id: organization.id,
                },
                body: {
                    distance_calculation_mode: nextValue,
                },
            });

            toast.success('Distance calculation mode updated', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to update distance calculation mode', {
                persistNotification: false,
            });
        } finally {
            await mutateOrganization();
            setIsUpdatingDistanceCalculationMode(false);
        }
    };

    const handleDelete = async () => {
        if (!organization) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete ${organization.name}? This action cannot be undone.`,
        );

        if (!confirmed) {
            return;
        }

        setIsDeleting(true);

        try {
            await getApiClient().delete({
                url: '/admin/organizations/{organization_id}',
                pathParams: { organization_id: organizationId },
            });

            toast.success('Organization deleted successfully', {
                persistNotification: false,
            });
            mutate(
                (key) =>
                    typeof key === 'string' &&
                    key.includes('/admin/organizations'),
            );
            router.navigate({ to: '/admin/organizations' });
        } catch (error) {
            toast.error('Failed to delete organization', {
                persistNotification: false,
            });
            setIsDeleting(false);
        }
    };

    const trimmedAssistantName = assistantName.trim();
    const resolvedAssistantName = trimmedAssistantName || 'Phoebe';
    const isAssistantNameDirty = trimmedAssistantName !== initialAssistantName;

    if (isLoadingOrganization) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading organization details…</span>
                </div>
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                Organization not found
            </div>
        );
    }

    return (
        <div className="min-w-0 space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Building2 className="text-muted-foreground/70 h-6 w-6" />
                    <h1 className="text-2xl font-semibold">
                        {organization.name}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={isSeeding}
                        onClick={async () => {
                            if (
                                !window.confirm(
                                    'This will add any missing sandbox records. It will not overwrite existing data. Continue?',
                                )
                            ) {
                                return;
                            }
                            setIsSeeding(true);
                            try {
                                const result = await getApiClient().post({
                                    url: '/admin/organizations/{organization_id}/sandbox/seed',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                });
                                toast.success(
                                    `Seeded sandbox: ${result.caregivers_created} caregivers, ${result.clients_created} clients, ${result.shifts_created} shifts, ${result.voice_calls_created} voice calls, ${result.activity_events_created} activity events created`,
                                    { persistNotification: false },
                                );
                            } catch (error) {
                                toast.error(
                                    `Failed to seed sandbox: ${error instanceof Error ? error.message : 'Unknown error'}`,
                                    { persistNotification: false },
                                );
                            } finally {
                                setIsSeeding(false);
                            }
                        }}
                    >
                        {isSeeding ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <TestTube2 className="mr-2 h-4 w-4" />
                        )}
                        {isSeeding ? 'Seeding…' : 'Re-seed Sandbox'}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsActivitySheetOpen(true)}
                    >
                        <Activity className="mr-2 h-4 w-4" /> Activity
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditModalOpen(true)}
                    >
                        <Edit className="mr-2 h-4 w-4" /> Edit
                    </Button>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </div>
            </div>
            <Sheet
                open={isActivitySheetOpen}
                onOpenChange={setIsActivitySheetOpen}
            >
                <SheetContent className="w-[480px] overflow-y-auto sm:max-w-[480px]">
                    <SheetHeader>
                        <SheetTitle>Organization activity</SheetTitle>
                    </SheetHeader>
                    <EntityEventsTimeline
                        organizationId={organization.id}
                        entityType="organization"
                        entityId={organization.id}
                        emptyMessage="No admin activity recorded yet."
                        scope="admin"
                    />
                </SheetContent>
            </Sheet>
            <Card className="p-6">
                <FeatureFlagToggle
                    label="VoIP Integration Setup Complete"
                    description={
                        organization.has_voip_integration
                            ? 'A connected provider account was detected. Mark setup complete after forwarding and configuration are finished; this hides the Phone setup tab.'
                            : 'No connected provider account was detected. Mark setup complete only when an externally managed VoIP setup is fully configured; this hides the Phone setup tab.'
                    }
                    featureKey="voip_integration_setup_complete"
                    organization={organization}
                />
            </Card>
            <Card className="p-6">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Organization ID
                        </h3>
                        <p className="mt-1 font-mono text-sm">
                            {organization.id}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Created At
                        </h3>
                        <p className="mt-1">
                            {new Date(
                                organization.created_at,
                            ).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Internal Name
                        </h3>
                        <p className="mt-1">
                            {organization.internal_name ?? (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Timezone
                        </h3>
                        <p className="mt-1">{organization.timezone}</p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Stripe Customer ID
                        </h3>
                        <p className="mt-1 font-mono text-sm">
                            {organization.stripe_customer_id ?? (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Stripe Subscription ID
                        </h3>
                        <p className="mt-1 font-mono text-sm">
                            {organization.stripe_subscription_id ?? (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Subscription Status
                        </h3>
                        <p className="mt-1">
                            {organization.subscription_status === 'none' ? (
                                <span className="text-muted-foreground">-</span>
                            ) : (
                                <Badge
                                    variant={
                                        organization.subscription_status ===
                                        'active'
                                            ? 'default'
                                            : organization.subscription_status ===
                                                'pending'
                                              ? 'secondary'
                                              : organization.subscription_status ===
                                                  'past_due'
                                                ? 'destructive'
                                                : organization.subscription_status ===
                                                    'canceled'
                                                  ? 'outline'
                                                  : 'secondary'
                                    }
                                >
                                    {organization.subscription_status.toUpperCase()}
                                </Badge>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Customer Lifecycle Stage
                        </h3>
                        <p className="mt-1">
                            {organization.customer_lifecycle_stage ? (
                                <Badge
                                    variant={
                                        organization.customer_lifecycle_stage ===
                                        'live'
                                            ? 'default'
                                            : organization.customer_lifecycle_stage ===
                                                'churned'
                                              ? 'destructive'
                                              : organization.customer_lifecycle_stage ===
                                                  'internal'
                                                ? 'outline'
                                                : 'secondary'
                                    }
                                >
                                    {formatLifecycleStageLabel(
                                        organization.customer_lifecycle_stage,
                                    )}
                                </Badge>
                            ) : (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Training Mode
                        </h3>
                        <div className="mt-2 flex items-center gap-3">
                            <Switch
                                id="organization-training-mode"
                                checked={organization.training_mode_enabled}
                                disabled={
                                    isUpdatingTrainingMode ||
                                    // Enabling requires an implementing org,
                                    // but turning training off is always
                                    // allowed (e.g. after the org goes live).
                                    (organization.customer_lifecycle_stage !==
                                        'implementing' &&
                                        !organization.training_mode_enabled)
                                }
                                onCheckedChange={(checked) =>
                                    void handleToggleTrainingMode(checked)
                                }
                            />
                            <Label
                                htmlFor="organization-training-mode"
                                className="cursor-pointer"
                            >
                                Training Mode
                            </Label>
                            {organization.training_mode_enabled &&
                            organization.customer_lifecycle_stage ===
                                'implementing' &&
                            typeof organization.training_mode_started_at ===
                                'string' ? (
                                <Badge variant="secondary">
                                    Day{' '}
                                    {trainingDayNumber(
                                        organization.training_mode_started_at,
                                        organization.timezone,
                                    )}
                                </Badge>
                            ) : null}
                            {isInTrainingMode(organization) ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isTrainingModeActionBusy}
                                    onClick={() => handleRestartTrainingMode()}
                                >
                                    Restart window
                                </Button>
                            ) : null}
                            {isInternalOrg ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isTrainingModeActionBusy}
                                    onClick={() => setIsSeedDialogOpen(true)}
                                >
                                    Seed test data
                                </Button>
                            ) : null}
                        </div>
                        <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                            Under development — do not enable Training Mode for
                            customer organizations.
                        </p>
                        <Dialog
                            open={isSeedDialogOpen}
                            onOpenChange={(open) => {
                                if (!open && isSeedingTrainingData) {
                                    // Ignore Escape/backdrop dismissal while
                                    // the seed request is still in flight.
                                    return;
                                }
                                setIsSeedDialogOpen(open);
                                if (!open) {
                                    setSeedCaregiverCount(0);
                                    setSeedClientCount(0);
                                    setSeedShiftCount(0);
                                }
                            }}
                        >
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Seed test data</DialogTitle>
                                    <DialogDescription>
                                        Generate additional synthetic
                                        caregivers, clients, and shifts in live
                                        or Test Mode. Up to{' '}
                                        {MAX_SEED_COUNT_PER_ENTITY} of each per
                                        request, on top of what already exists.
                                        For a full demo dataset in Test Mode
                                        only, use “Seed sandbox” above instead.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="seed-mode">Mode</Label>
                                        <Select
                                            value={seedMode}
                                            onValueChange={(value) => {
                                                if (isSeedMode(value)) {
                                                    setSeedMode(value);
                                                }
                                            }}
                                        >
                                            <SelectTrigger id="seed-mode">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="live">
                                                    Live
                                                </SelectItem>
                                                <SelectItem value="sandbox">
                                                    Sandbox
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="space-y-2">
                                            <Label htmlFor="seed-caregivers">
                                                Caregivers
                                            </Label>
                                            <Input
                                                id="seed-caregivers"
                                                type="number"
                                                min={0}
                                                max={MAX_SEED_COUNT_PER_ENTITY}
                                                value={seedCaregiverCount}
                                                onChange={(event) =>
                                                    setSeedCaregiverCount(
                                                        clampSeedCount(
                                                            event.target.value,
                                                        ),
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="seed-clients">
                                                Clients
                                            </Label>
                                            <Input
                                                id="seed-clients"
                                                type="number"
                                                min={0}
                                                max={MAX_SEED_COUNT_PER_ENTITY}
                                                value={seedClientCount}
                                                onChange={(event) =>
                                                    setSeedClientCount(
                                                        clampSeedCount(
                                                            event.target.value,
                                                        ),
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="seed-shifts">
                                                Shifts
                                            </Label>
                                            <Input
                                                id="seed-shifts"
                                                type="number"
                                                min={0}
                                                max={MAX_SEED_COUNT_PER_ENTITY}
                                                value={seedShiftCount}
                                                onChange={(event) =>
                                                    setSeedShiftCount(
                                                        clampSeedCount(
                                                            event.target.value,
                                                        ),
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button
                                        disabled={
                                            isSeedingTrainingData ||
                                            (seedCaregiverCount === 0 &&
                                                seedClientCount === 0 &&
                                                seedShiftCount === 0)
                                        }
                                        onClick={() =>
                                            void seedTrainingModeData()
                                        }
                                    >
                                        {isSeedingTrainingData
                                            ? 'Seeding…'
                                            : 'Seed'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                        <Dialog
                            open={isRestartDialogOpen}
                            onOpenChange={(open) => {
                                if (!open && isUpdatingTrainingMode) {
                                    // Ignore Escape/backdrop dismissal while
                                    // the restart/wipe request is in flight.
                                    return;
                                }
                                setIsRestartDialogOpen(open);
                                if (!open) {
                                    setWipeConfirmText('');
                                }
                            }}
                        >
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Restart training</DialogTitle>
                                    <DialogDescription>
                                        Restart the window only, or wipe all
                                        learned data first. Agent-run history is
                                        kept either way.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="flex items-center justify-between gap-3 rounded-md border p-4">
                                    <div>
                                        <p className="text-sm font-medium">
                                            Restart window only
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            Begins a fresh 21-day window and
                                            keeps everything Phoebe learned.
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        disabled={isUpdatingTrainingMode}
                                        onClick={() =>
                                            void restartTrainingWindow()
                                        }
                                    >
                                        Restart window
                                    </Button>
                                </div>
                                <div className="border-destructive/40 space-y-3 rounded-md border p-4">
                                    <p className="text-sm font-medium">
                                        Wipe all learned
                                    </p>
                                    <p className="text-muted-foreground text-sm">
                                        Permanently deletes memory, notes,
                                        playbook rules, outreach filters, live
                                        availability, and QA conversations, then
                                        restarts the window. This can’t be
                                        undone.
                                    </p>
                                    <Label
                                        htmlFor="wipe-confirm"
                                        className="text-sm font-normal"
                                    >
                                        Type the organization name (
                                        {organization.name}) to confirm
                                    </Label>
                                    <Input
                                        id="wipe-confirm"
                                        value={wipeConfirmText}
                                        onChange={(event) =>
                                            setWipeConfirmText(
                                                event.target.value,
                                            )
                                        }
                                        placeholder={organization.name}
                                        autoComplete="off"
                                    />
                                    <div className="flex justify-end">
                                        <Button
                                            variant="destructive"
                                            disabled={
                                                isUpdatingTrainingMode ||
                                                wipeConfirmText.trim() !==
                                                    organization.name
                                            }
                                            onClick={() =>
                                                void wipeAndRestartTraining()
                                            }
                                        >
                                            Wipe all learned
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                        {organization.customer_lifecycle_stage !==
                            'implementing' &&
                        organization.training_mode_enabled ? (
                            <p className="text-muted-foreground mt-1 text-xs">
                                Paused while the organization isn’t implementing
                                — members don’t see training surfaces. The start
                                date is kept; turn training off to stop it.
                            </p>
                        ) : organization.customer_lifecycle_stage !==
                          'implementing' ? (
                            <p className="text-muted-foreground mt-1 text-xs">
                                Available while the organization is
                                implementing.
                            </p>
                        ) : null}
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Signed Kickoff Date
                        </h3>
                        <p className="mt-1">
                            {formatBusinessDate(
                                organization.customer_signed_kickoff_date,
                            ) ?? (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Customer Live Date
                        </h3>
                        <p className="mt-1">
                            {formatBusinessDate(
                                organization.customer_live_date,
                            ) ?? (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Account Type
                        </h3>
                        <div className="mt-2 flex items-center gap-3">
                            <Switch
                                id="organization-sub-account"
                                checked={subAccount?.is_sub_account ?? false}
                                disabled={
                                    isLoadingSubAccount ||
                                    isUpdatingSubAccount ||
                                    !!subAccountError
                                }
                                onCheckedChange={(checked) =>
                                    void handleToggleSubAccount(checked)
                                }
                            />
                            <Label
                                htmlFor="organization-sub-account"
                                className="cursor-pointer"
                            >
                                Sub Account
                            </Label>
                            {subAccount?.is_sub_account ? (
                                <Badge variant="secondary">Sub Account</Badge>
                            ) : null}
                        </div>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Customer Churn Entry Date
                        </h3>
                        <p className="mt-1">
                            {formatBusinessDate(
                                organization.customer_churned_date,
                            ) ?? (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            EHR
                        </h3>
                        <p className="mt-1">
                            {formatEhrTypeLabel(organization.ehr_type) ?? (
                                <span className="text-muted-foreground">—</span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            VoIP Provider
                        </h3>
                        <p className="mt-1">
                            {formatVoipProviderLabel(
                                organization.voip_provider,
                            ) ?? (
                                <span className="text-muted-foreground">—</span>
                            )}
                        </p>
                    </div>
                    <div className="border-t pt-4 md:col-span-2">
                        <h3 className="text-sm font-medium">
                            BAA / Compliance
                        </h3>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Signed BAA
                        </h3>
                        <p className="mt-1">
                            <Badge
                                variant={
                                    organization.baa_signed
                                        ? 'default'
                                        : 'secondary'
                                }
                            >
                                {organization.baa_signed ? 'Yes' : 'No'}
                            </Badge>
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            BAA Notice Mode
                        </h3>
                        <p className="mt-1">
                            {organization.baa_notice_mode ? (
                                <Badge variant="secondary">
                                    {organization.baa_notice_mode === 'push'
                                        ? 'Push'
                                        : 'Opt-in'}
                                </Badge>
                            ) : (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                    <div>
                        <h3 className="text-muted-foreground text-sm font-medium">
                            Notice Contact Email
                        </h3>
                        <p className="mt-1">
                            {organization.notice_contact_email ?? (
                                <span className="text-muted-foreground">
                                    Not set
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </Card>
            {organization.customer_account ? (
                <Card className="p-6" data-testid="customer-account-card">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <Building2 className="text-muted-foreground h-5 w-5" />
                                <h2 className="text-lg font-semibold">
                                    Customer Account
                                </h2>
                            </div>
                            <p className="mt-2 text-xl font-medium">
                                {organization.customer_account.name}
                            </p>
                            <p className="text-muted-foreground mt-1 text-sm">
                                Explicit business grouping; EHR matches below
                                are technical connection evidence only.
                            </p>
                        </div>
                        <Badge variant="secondary">
                            {organization.customer_account.organization_count}{' '}
                            {organization.customer_account
                                .organization_count === 1
                                ? 'organization'
                                : 'organizations'}
                        </Badge>
                    </div>
                    <div className="mt-5 space-y-2">
                        {(
                            organization.customer_account_organizations ?? []
                        ).map((accountOrganization) => {
                            const ehrLabel = formatEhrConnectionLabel(
                                accountOrganization.ehr_connection_type ??
                                    accountOrganization.ehr_type,
                            );
                            return (
                                <div
                                    key={accountOrganization.id}
                                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
                                >
                                    <div className="min-w-0">
                                        <Link
                                            to="/admin/organizations/$organization-id"
                                            params={{
                                                'organization-id':
                                                    accountOrganization.id,
                                            }}
                                            className="font-medium hover:underline"
                                        >
                                            {accountOrganization.internal_name ??
                                                accountOrganization.name}
                                        </Link>
                                        {accountOrganization.id ===
                                        organization.id ? (
                                            <span className="text-muted-foreground ml-2 text-xs">
                                                Current
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline">
                                            {ehrLabel ?? 'EHR not configured'}
                                        </Badge>
                                        {accountOrganization.confirmed_shared_ehr_connection ? (
                                            <Badge variant="secondary">
                                                Same credentialed EHR connection
                                            </Badge>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <CustomerAccountNotificationDestinations
                        customerAccountId={organization.customer_account.id}
                        currentOrganizationId={organization.id}
                        organizations={
                            organization.customer_account_organizations ?? []
                        }
                    />
                </Card>
            ) : null}
            <InternalDailyDigestSlackChannelSection
                organization={organization}
            />
            <div>
                <div className="mb-4">
                    <div className="flex items-center gap-2">
                        <ClipboardList className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">Onboarding</h2>
                    </div>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Customer setup progress and the internal checklists CX
                        uses to get the organization live.
                    </p>
                </div>
                <div className="space-y-6">
                    <OnboardingReviewCard organizationId={organizationId} />
                    <OrganizationChecklistPanel
                        organizationId={organizationId}
                        checklistId="pre_onboarding"
                        title="Customer setup form (pre-onboarding)"
                        description="Mirror of the setup checklist the customer works through during pre-onboarding (team, company info, phone intake, EHR access)."
                    />
                    <OrganizationChecklistPanel
                        organizationId={organizationId}
                        checklistId="onboarding"
                        title="Internal deployment checklist"
                        description="Phoebe-internal implementation checklist for getting the organization live (Initial Setup / Train Phoebe / Go Live). Not shown to the customer."
                    />
                    <OrganizationChecklistPanel
                        organizationId={organizationId}
                        checklistId="post_onboarding"
                        title="Internal follow-up checklist (post-onboarding)"
                        description="Phoebe-internal follow-up work after the organization goes live."
                    />
                </div>
            </div>
            <SchedulerAssessmentsPanel organizationId={organizationId} />
            <StaffImessageNotificationBudgetSection
                organization={organization}
            />
            <div>
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Building2 className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">
                            A2P Compliance Profile
                        </h2>
                    </div>
                </div>
                <Card className="p-6">
                    <A2pComplianceProfilePanel
                        a2pComplianceProfile={a2pComplianceProfile}
                        isLoading={isLoadingA2pComplianceProfile}
                        isDeleting={isDeletingA2p}
                        isRegenerating={isRegeneratingA2p}
                        onOpenModal={() => setIsA2pModalOpen(true)}
                        onRegenerate={async () => {
                            if (!organizationId) {
                                return;
                            }
                            setIsRegeneratingA2p(true);
                            try {
                                await getApiClient().post({
                                    url: '/admin/organizations/{organization_id}/a2p-compliance-profile/regenerate-legal-documents',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                });
                                toast.success(
                                    'A2P ToS and Privacy Policy regeneration started; opt-in form URL will be kept',
                                    { persistNotification: false },
                                );
                                await mutateA2pComplianceProfile();
                            } catch {
                                toast.error(
                                    'Failed to regenerate A2P legal documents',
                                    {
                                        persistNotification: false,
                                    },
                                );
                            } finally {
                                setIsRegeneratingA2p(false);
                            }
                        }}
                        onDelete={async () => {
                            if (!organizationId) {
                                return;
                            }
                            setIsDeletingA2p(true);
                            try {
                                await getApiClient().delete({
                                    url: '/admin/organizations/{organization_id}/a2p-compliance-profile',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                });
                                toast.success('A2P profile deleted', {
                                    persistNotification: false,
                                });
                                await mutateA2pComplianceProfile();
                            } catch {
                                toast.error('Failed to delete A2P profile', {
                                    persistNotification: false,
                                });
                            } finally {
                                setIsDeletingA2p(false);
                            }
                        }}
                    />
                </Card>
            </div>
            <div>
                <div className="mb-4">
                    <div className="flex items-center gap-2">
                        <Building2 className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">
                            Twilio A2P Registration
                        </h2>
                    </div>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Manual mode: Twilio provisioning must be done manually.
                        Auto mode: Automated provisioning.
                    </p>
                </div>
                <Card className="p-6">
                    <TwilioA2pRegistrationPanel
                        a2pComplianceProfile={a2pComplianceProfile}
                        twilioA2pRegistration={twilioA2pRegistration}
                        isLoading={isLoadingTwilioA2pRegistration}
                        isSaving={isSavingTwilioA2p}
                        isStarting={isStartingTwilioA2p}
                        isPurging={isPurgingTwilioA2p}
                        onSave={async (values) => {
                            if (!organizationId) {
                                return;
                            }
                            setIsSavingTwilioA2p(true);
                            try {
                                await getApiClient().put({
                                    url: '/admin/organizations/{organization_id}/twilio-a2p-registration',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                    body: values,
                                });
                                toast.success('Registration settings saved', {
                                    persistNotification: false,
                                });
                                await mutateTwilioA2pRegistration();
                            } catch (e) {
                                toast.error(
                                    'Failed to save registration settings',
                                    { persistNotification: false },
                                );
                            } finally {
                                setIsSavingTwilioA2p(false);
                            }
                        }}
                        onStart={async () => {
                            if (!organizationId) {
                                return;
                            }
                            setIsStartingTwilioA2p(true);
                            try {
                                await getApiClient().post({
                                    url: '/admin/organizations/{organization_id}/twilio-a2p-registrations/provision',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                });
                                toast.success('Provisioning started', {
                                    persistNotification: false,
                                });
                                await mutateTwilioA2pRegistration();
                            } catch (e) {
                                toast.error('Failed to start provisioning', {
                                    persistNotification: false,
                                });
                            } finally {
                                setIsStartingTwilioA2p(false);
                            }
                        }}
                        onPurge={async () => {
                            if (!organizationId) {
                                return;
                            }
                            if (
                                !confirm(
                                    'This will delete Twilio resources (dev only). Continue?',
                                )
                            ) {
                                return;
                            }
                            setIsPurgingTwilioA2p(true);
                            try {
                                await getApiClient().post({
                                    url: '/admin/organizations/{organization_id}/twilio-a2p-registration/purge',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                });
                                toast.success(
                                    'Purged Twilio A2P registration (dev)',
                                    {
                                        persistNotification: false,
                                    },
                                );
                                await mutateTwilioA2pRegistration();
                            } catch (error) {
                                toast.error(
                                    'Failed to purge Twilio A2P registration',
                                    {
                                        persistNotification: false,
                                    },
                                );
                            } finally {
                                setIsPurgingTwilioA2p(false);
                            }
                        }}
                        isSendingTestText={isSendingTestText}
                        onSendTestText={async () => {
                            if (!organizationId) {
                                return;
                            }
                            setIsSendingTestText(true);
                            try {
                                await getApiClient().post({
                                    url: '/admin/organizations/{organization_id}/twilio-a2p-registration/texting-canary',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                });
                                await mutateTwilioA2pRegistration();
                                toast.success('Test text sent', {
                                    persistNotification: false,
                                });
                            } catch (caught) {
                                toast.error(
                                    serverErrorDetail(caught) ??
                                        'Failed to send test text',
                                    {
                                        persistNotification: false,
                                    },
                                );
                            } finally {
                                setIsSendingTestText(false);
                            }
                        }}
                        onRefreshRegistration={async () => {
                            await mutateTwilioA2pRegistration();
                        }}
                    />
                </Card>
            </div>
            <div>
                <h2 className="mb-4 text-lg font-semibold">Feature Flags</h2>
                <Card>
                    <CardContent className="p-6">
                        <div className="divide-border/60 -my-3 divide-y [&>*]:py-3">
                            <CaregiverSmsAgentModeControl
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Amazon Location Driving"
                                description="Use Amazon road routes for caregiver-to-client travel distances throughout Phoebe. When on, this overrides the Distance Calculation Mode below, including Straight-line and Mapbox Driving. Walking and transit routes remain available regardless of this flag."
                                featureKey="amazon_location_driving_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Caregiver iMessage"
                                description="Route caregivers for this organization through the caregiver agent over configured Photon iMessage lines. Off uses the organization's SMS sender."
                                featureKey="caregiver_imessage_enabled"
                                organization={organization}
                            />
                            <div className="border-border bg-muted/50 rounded-md border p-3">
                                <QaVoiceAgentSettingsPanel
                                    organizationId={organization.id}
                                />
                            </div>
                            <FeatureFlagToggle
                                label="Enable Outreach Sending"
                                description="Allow users to send outreach messages to caregivers. When off, users can still create and review pending outreach with agent suggestions, but cannot start sending."
                                featureKey="outreach_sending_enabled"
                                organization={organization}
                            />
                            <OutreachSenderAllowlist
                                organization={organization}
                                members={members ?? []}
                                isLoadingMembers={isLoadingMembers}
                                onSaved={mutateOrganization}
                            />
                            {organization.admin_agent_tool_search_eligible ? (
                                <FeatureFlagToggle
                                    label="Admin Agent Tool Search"
                                    description="Enable Anthropic-native discovery for deferred admin-agent tools on internal Phoebe or explicitly allowlisted organizations."
                                    featureKey="admin_agent_tool_search"
                                    organization={organization}
                                />
                            ) : null}
                            <FeatureFlagToggle
                                label="Email Daily Digest"
                                description="Enable the daily email digest for this organization."
                                featureKey="email_daily_digest_enabled"
                                organization={organization}
                                checkedOverride={
                                    organization.daily_digest_settings
                                        ?.enabled ?? false
                                }
                                onCheckedChangeOverride={async (checked) => {
                                    await mutateOrganization((currentData) => {
                                        if (!currentData) {
                                            return currentData;
                                        }
                                        return {
                                            ...currentData,
                                            daily_digest_settings: {
                                                ...(currentData.daily_digest_settings ?? {
                                                    recipient_emails: [],
                                                }),
                                                enabled: checked,
                                            },
                                        };
                                    }, false);

                                    try {
                                        const updatedOrganization =
                                            await getApiClient().put({
                                                url: '/admin/organizations/{organization_id}',
                                                pathParams: {
                                                    organization_id:
                                                        organization.id,
                                                },
                                                body: {
                                                    daily_digest_settings: {
                                                        enabled: checked,
                                                    },
                                                },
                                            });
                                        if (updatedOrganization) {
                                            await mutateOrganization(
                                                updatedOrganization,
                                                false,
                                            );
                                        }
                                        toast.success(
                                            'Feature flag updated successfully',
                                            {
                                                persistNotification: false,
                                            },
                                        );
                                    } catch {
                                        toast.error(
                                            'Failed to update feature flag',
                                            {
                                                persistNotification: false,
                                            },
                                        );
                                        await mutateOrganization();
                                    }
                                }}
                            />
                            <FeatureFlagToggle
                                label="Caregiver Points"
                                description="Enable caregiver point awards, leaderboard/profile points, manual adjustments, and CSV import/export."
                                featureKey="caregiver_points_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Last-name sorting"
                                description="Order clients and caregivers throughout the app alphabetically by last name."
                                featureKey="last_name_sorting_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Enable Shift Editing"
                                description="Allow users to edit shifts for this organization."
                                featureKey="edit_shift_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Agent Shift Creation"
                                description="Let the general agent create one-off shifts from chat and permanently delete the ones it created, both approval-gated. Creation also writes to the source EHR when EHR Shift Creation Writeback and the account’s creation switch are both on. Leave off for organizations whose EHR has no shift creation writeback."
                                featureKey="agent_shift_creation_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="EHR Shift Creation Writeback"
                                description="Write new one-off shifts to the connected EHR when the account creation switch is also enabled."
                                featureKey="ehr_shift_creation_writeback_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Auto Seed Sandbox"
                                description="Seed sandbox data when creating this organization and keep generated sandbox shifts and outreaches fresh overnight."
                                featureKey="auto_seed_sandbox"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Hide Test Mode Toggle"
                                description="Hide the sidebar Live/Test toggle and the floating Test Mode Active banner for this organization, and force the app back to Live mode if it is ever in Test mode."
                                featureKey="hide_test_mode_toggle"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Dark Mode"
                                description="Let users choose Light, Dark, or System appearance. Light remains the default; each browser remembers its choice."
                                featureKey="dark_mode_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Parallel Sync (per-entity)"
                                description="Skip the per-org sync serializer so caregiver, client, shift, and care-coordinator syncs can run concurrently for this org. Per-entity locks (vendor rate-limit safety) still apply. Leave OFF unless rolling out."
                                featureKey="parallel_sync_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Automatic EHR Shift Documentation"
                                description="Automatically document Phoebe’s work on source EHR shifts. AxisCare: maintain callout summaries in the Internal Memo. WellSky: add a staffed-via-Phoebe note after Phoebe fills a shift. Does not enable coordinator-initiated note writes."
                                featureKey="shift_memo_writeback_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Imported Care Plan Documents"
                                description="Append imported care-plan document text (OCR) beneath the EHR-owned care plan, preserved across every EHR sync."
                                featureKey="care_plan_document_supplement_enabled"
                                organization={organization}
                            />
                            <div className="border-border bg-muted/50 space-y-4 rounded-md border p-3">
                                <h3 className="text-base font-semibold">
                                    Clock In/Out
                                </h3>
                                <FeatureFlagToggle
                                    label="Enable Clock In/Out Reminders"
                                    description="Enable clock in/out reminders"
                                    featureKey="clock_in_out_processing_enabled"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Allow Clock-In Writes"
                                    description="Show the clock-in button and write clock-in events back to the source EHR system."
                                    featureKey="allow_clock_in_write"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Allow Clock-Out Writes"
                                    description="Show the clock-out button and write clock-out events back to the source EHR system."
                                    featureKey="allow_clock_out_write"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Allow Inbound Voice Clock-In Writes"
                                    description="Let inbound voice calls write clock-in events when the caller has exactly one eligible shift."
                                    featureKey="voice_inbound_clock_in_write_enabled"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Allow Inbound Voice Clock-Out Writes"
                                    description="Let inbound voice calls write clock-out events when the caller has exactly one clocked-in shift."
                                    featureKey="voice_inbound_clock_out_write_enabled"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Allow Outbound Voice Clock-In Reminder Writes"
                                    description="Let outbound clock-in reminder calls write clock-in events for the shift the reminder targets."
                                    featureKey="voice_outbound_clock_in_write_enabled"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Allow Outbound Voice Clock-Out Reminder Writes"
                                    description="Let outbound clock-out reminder calls write clock-out events for the shift the reminder targets."
                                    featureKey="voice_outbound_clock_out_write_enabled"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Shift Feedback Memory"
                                    description="Save shift debrief answers from clock-out reminder calls into the caregiver's memory."
                                    featureKey="shift_feedback_memory_enabled"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Voice: Availability Ask On Decline"
                                    description="After a callout decline, ask once whether any days or times in the next couple of weeks usually work."
                                    featureKey="voice_availability_ask_on_decline_enabled"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Clock-Out Reminder Uses Clock-In Time"
                                    description="Trigger clock-out reminders at the caregiver's clock-in time plus the shift's scheduled duration, instead of the shift's scheduled end time."
                                    featureKey="use_clock_in_time_for_clock_out_reminder"
                                    organization={organization}
                                />
                                <FeatureFlagToggle
                                    label="Require Clock-In for Clock-Out Reminder"
                                    description="Only send scheduled clock-out reminders after the caregiver has clocked in. Leave off for agencies that still want clock-out nudges when a caregiver never clocked in."
                                    featureKey="require_clock_in_for_clock_out_reminder"
                                    organization={organization}
                                />
                                <ClockSettingNumberInput
                                    organization={organization}
                                    settingKey="clock_in_grace_minutes"
                                    label="Clock-In Grace Period (minutes)"
                                    description={`Accept a stated clock-in time up to this many minutes after the caregiver reaches out, so someone calling a few minutes before their shift can clock in at their scheduled start. 0 disables the grace period; max ${MAX_CLOCK_GRACE_MINUTES}.`}
                                    min={0}
                                    max={MAX_CLOCK_GRACE_MINUTES}
                                />
                                <ClockSettingNumberInput
                                    organization={organization}
                                    settingKey="clock_out_grace_minutes"
                                    label="Clock-Out Grace Period (minutes)"
                                    description={`The same allowance for a stated clock-out time against the scheduled shift end. Usually left at 0 even when clock-in grace is set, since most agencies want early clock-outs rejected. Max ${MAX_CLOCK_GRACE_MINUTES}.`}
                                    min={0}
                                    max={MAX_CLOCK_GRACE_MINUTES}
                                />
                                <ClockSettingNumberInput
                                    organization={organization}
                                    settingKey="clock_reminder_monitor_window_hours"
                                    label="Missed Clock In/Out EHR Check Window (hours)"
                                    description={`How many hours after the scheduled clock in/out time Phoebe keeps checking the EHR for a late punch before marking the reminder missed. Checks run every 5 minutes for the first hour, then hourly. 1 stops one hour after the scheduled time (the default); max ${MAX_CLOCK_REMINDER_MONITOR_WINDOW_HOURS}.`}
                                    min={
                                        MIN_CLOCK_REMINDER_MONITOR_WINDOW_HOURS
                                    }
                                    max={
                                        MAX_CLOCK_REMINDER_MONITOR_WINDOW_HOURS
                                    }
                                />
                            </div>
                            <FeatureFlagToggle
                                label="Use Patient Initials"
                                description="Show patient initials (e.g., 'J.S.') instead of full names in voice calls and other non-shift-SMS flows. Caregiver shift texts always use initials."
                                featureKey="use_patient_initials"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Auto-Send General Replies"
                                description="Auto-send SMS general replies (no active shift, callout, or survey). When disabled, general replies are staged for coordinator review."
                                featureKey="auto_send_general_replies"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="SMS Urgent Call-the-Office Replies"
                                description="Emergency or sensitive disclosures by text (in the hospital, accident, assault or domestic violence, death in the family, feeling unsafe) get a brief acknowledgement plus “call the office right away” sent immediately, with an urgent activity-feed item for staff. Also sends Phoebe’s default disabled-SMS-cancellation reply immediately. Off keeps the emergency rules inactive and the default cancellation reply staged."
                                featureKey="sms_urgent_call_office_replies_enabled"
                                organization={organization}
                            />
                            <SmsShiftCancellationSettingToggle
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Inbound Cancellation Playbook"
                                description="Let the Inbound Cancellation Handling playbook steer how Phoebe responds when a caregiver calls or texts to cancel an upcoming shift. Inert unless SMS shift cancellation and/or the inbound-voice cancellation flow is enabled."
                                featureKey="inbound_cancellation_playbook_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="EHR Caregiver Unassignment Writeback"
                                description="Write scheduler-initiated caregiver removals back to the source EHR. When disabled, those removals are blocked. Caregiver cancellations write back whenever cancellation is enabled for their channel, regardless of this flag."
                                featureKey="ehr_caregiver_unassignment_writeback_enabled"
                                organization={organization}
                                checkedOverride={isEhrCaregiverUnassignmentWritebackEnabled(
                                    organization.features,
                                )}
                            />
                            <FeatureFlagToggle
                                label="Agent Shift Cancel"
                                description="Let the general agent cancel shifts from chat (approval-gated). EHR-linked shifts are hard-cancelled in the source EHR with office or caregiver attribution; Phoebe-owned shifts are cancelled locally. Off is the kill switch."
                                featureKey="agent_shift_cancel_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="EHR Availability Writeback"
                                description="Write caregiver unavailability changes back to the source EHR for supported providers. When disabled, availability changes remain local to Phoebe."
                                featureKey="ehr_availability_writeback_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="EHR Shift Time Writeback"
                                description="Write supported shift start and end time changes back to the source EHR. Unsupported recurrence scopes fail closed instead of silently remaining local to Phoebe."
                                featureKey="ehr_shift_time_writeback_enabled"
                                organization={organization}
                            />
                            <SmsInlineReviewModeSelect
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Agent Subagents"
                                description="Allow enabled Phoebe agents to create, message, list, and inspect bounded child subagent runs."
                                featureKey="agent_sub_agents_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Agent v3 (minimal clean agent)"
                                description="Route this organization's general sidebar chat runs to the minimal v3 agent build instead of the current production agent."
                                featureKey="agent_v3_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Coordinator automations"
                                description="Show the Automations page and give chat the manage_automation tool, and let scheduled and event automations fire. Requires Agent v3."
                                featureKey="coordinator_automations_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Chat Settings Editing"
                                description="Sidebar chat can surface org general settings (auto-reply and mark-as-read toggles) as live inline toggle cards coordinators edit in place. Off by default; the platform-admin settings write skill is unaffected."
                                featureKey="chat_settings_editing_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phoebe Thinking Tips"
                                description="Show a compact General Phoebe Tip line under Phoebe's live thinking status in general and outreach chat surfaces."
                                featureKey="phoebe_thinking_tips_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Client Notification On Fill"
                                description="Notify clients by voice when an open shift is filled."
                                featureKey="client_notification_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Notification Type Schedules"
                                description="Let teammates set delivery hours for each notification type. Enable only after all API and worker services support schedules and older services have drained. Turning this off hides editing; saved schedules still apply."
                                featureKey="notification_type_schedules_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Per-Contact Notification Channels"
                                description="Let each client contact be configured for call, text, or both, and choose which contact is the default. Requires client SMS notifications (PHO-11034); leave off until that ships."
                                featureKey="client_contact_notification_channels_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Require Care Plan Acknowledgement"
                                description="Voice open shifts will require caregivers to hear the care plan before accepting a shift."
                                featureKey="require_care_plan_acknowledgement"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Voice Tool-Call Typing Sounds"
                                description="Play caller-facing typing ambience while Phoebe runs voice tools."
                                featureKey="voice_tool_call_typing_noise_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Voice: Adaptive Speaking Delivery"
                                description="For new classic voice calls, use detected caller speaking speed to slow the voice to 80–100% of its configured default. Off by default; existing calls keep their starting setting."
                                featureKey="voice_adaptive_delivery_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Voice: Background Reasoning Specialist"
                                description="Let voice calls hand a difficult schedule comparison to a stronger reasoning model while the conversation continues, and speak its answer when it arrives. Off by default."
                                featureKey="voice_background_reasoning_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="SMS: Voice-Call Context"
                                description="Include recent voice-call context when handling inbound SMS for this organization."
                                featureKey="sms_voice_call_context_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="SMS: Agent Notes"
                                description="Inactive. The SMS engine no longer mounts a note tool, so this changes nothing today. Leave off."
                                featureKey="agent_notes_sms_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Voice: Runtime-Error SMS Continuation"
                                description="When an outbound call with a live person fails open to transfer, text the caregiver so the conversation continues over SMS with the call's context."
                                featureKey="voice_runtime_error_sms_continuation_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Talent Agent"
                                description="Matched caregivers who reach out by voice or SMS can hear or receive their top matched open shifts and express interest, routed to the scheduler for approval as Caregiver Shift Requests."
                                featureKey="shift_discovery_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Click-to-Call"
                                description="Let coordinators call caregivers from the app: Phoebe calls the coordinator's phone first, then bridges to the caregiver with the organization's outbound number as caller ID."
                                featureKey="click_to_call_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Click-to-Call: Browser Calling"
                                description="Place click-to-call calls through the browser (computer mic/speakers) instead of bridging via the coordinator's phone. Calls are recorded and transcribed after the call. Requires Click-to-Call."
                                featureKey="click_to_call_browser_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Inbound Browser Ring"
                                description="Ring present coordinators’ browsers before normal inbound routing when the matched call-handling schedule opts in."
                                featureKey="inbound_browser_ring_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Schedule Ring Groups"
                                description="Let an administrator narrow a call-handling schedule’s browser ring to selected team members. Off rings every present coordinator, as before."
                                featureKey="phone_system_ring_groups_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Browser Availability"
                                description="Show a personal browser-call availability control and publish ready-browser status. This does not change routing until Enforce Browser Availability is also enabled. Off shows no control or new availability traffic."
                                featureKey="phone_system_browser_availability_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Enforce Browser Availability"
                                description="Route inbound browser calls only to coordinators publishing a ready and available browser. Enable Browser Availability first so open tabs can publish before enforcement."
                                featureKey="phone_system_browser_availability_routing_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Browser Ring Duration"
                                description="Let each call-handling schedule choose how long browsers ring before normal routing continues. Off hides the control and always uses 15 seconds."
                                featureKey="phone_system_browser_ring_duration_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Tiered Browser Ring"
                                description="Order selected schedule ring-group members into sequential browser-ring tiers. Off rings the same selected members simultaneously. Requires Inbound Browser Ring and Schedule Ring Groups."
                                featureKey="phone_system_tiered_browser_ring_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Classic Voicemail"
                                description="Let named call-handling schedules record a conventional voicemail after the team does not answer."
                                featureKey="inbound_classic_voicemail_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Voice: Take a Message on Failed Transfer"
                                description="On by default. A busy or unanswered live transfer reconnects the caller to Phoebe, who takes a message for the team. Turn off to end those calls with an apology instead."
                                featureKey="failed_transfer_take_message_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Internal Slack: Spammy Follow-up Check-in"
                                description="On by default. Posts an internal Slack check-in when this org saves more than 3 follow-ups on an outreach, shift confirmations, or a default outreach sequence. Turn off to silence it for this organization."
                                featureKey="spammy_scheduler_config_alert_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phone System: Multi-Line Routing"
                                description="Show owned-line names and roles, and use per-line defaults for normal inbound and carrier fail-open routing. Off preserves the existing phone-number UI and routing behavior."
                                featureKey="phone_system_multi_line_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Care Coordinator Support"
                                description="Allow assigning users to be care coordinators and filtering shifts by care coordinator."
                                featureKey="care_coordinator_support_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Expanded Caregiver Memory Inputs"
                                description="Enable the expanded set of caregiver-memory input surfaces."
                                featureKey="expanded_memory_inputs_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Native Attributes"
                                description="Enable staged native-attribute ingress, including sync emission when that source is allowed."
                                featureKey="native_attributes_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Metrics"
                                description="Show the Metrics page in the navigation."
                                featureKey="metrics_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Metrics Open Hours"
                                description="Show the open hours chart on the Metrics page."
                                featureKey="metrics_open_hours_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Authorization Utilization"
                                description="Enable authorization utilization workflows and surfaces."
                                featureKey="authorizations_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Referrals"
                                description="Enable referral workflows for this organization."
                                featureKey="referrals_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Sidebar Sessions"
                                description="Show recent Phoebe agent sessions in the persistent app sidebar."
                                featureKey="sidebar_sessions_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Unified Activity Inbox"
                                description="Combine Activity and notifications in one Inbox with personal read state. Replaces the bell and top notification bar."
                                featureKey="unified_activity_inbox_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Inbox Schedule and Profile"
                                description="Show the Schedule and Profile sidebar in the Inbox. Requires Unified Activity Inbox."
                                featureKey="inbox_context_sidebar_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Top Notifications Bar"
                                description="Replace the desktop sidebar bell with a persistent top bar showing unread Shift updates, Messages, Alerts, Outreach, and All counts. Mobile keeps the compact sidebar bell."
                                featureKey="top_notifications_bar_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Payment Method Prompt"
                                description="Prompt post-onboarding customers without a payment method to complete the secure Stripe setup form."
                                featureKey="payment_method_prompt_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="ETE Callouts Notification UI"
                                description="Show the Phoebe asks first approval controls preview on the notifications settings page. UI-only; selections are not persisted yet."
                                featureKey="ete_callouts_notification_ui"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="RingCentral v2"
                                description="Show the redesigned stepped RingCentral setup card in Advanced settings instead of the legacy forwarding card."
                                featureKey="ringcentral_v2"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="RingCentral OAuth"
                                description="Show the RingCentral connect button in organization settings."
                                featureKey="ringcentral_oauth_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Dialpad OAuth"
                                description="Show the Dialpad connect button in organization settings."
                                featureKey="dialpad_oauth_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="GoTo OAuth"
                                description="Show the GoTo connect button in organization settings."
                                featureKey="goto_oauth_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Vonage OAuth"
                                description="Show the Vonage connect card in organization settings, which imports recorded office calls and their transcripts."
                                featureKey="vonage_oauth_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="RingCentral Manual Setup"
                                description="Show the manual RingCentral forwarding setup UI in organization settings."
                                featureKey="ringcentral_manual_setup_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="RingCentral Contact Sync"
                                description="Sync RingCentral contacts for unknown-number display names. Requires RingCentral OAuth and a connection granting the ReadContacts scope."
                                featureKey="ringcentral_contact_sync_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="RingCentral Texting"
                                description="On by default. Off stops RingCentral texting entirely for this organization — no ingesting, no replies — whatever each line’s own Texting and Replies switches say. Voice forwarding is unaffected."
                                featureKey="ringcentral_texting_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Slack OAuth"
                                description="Show the Slack connect button in organization settings."
                                featureKey="slack_oauth_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Generic Phone Adapter"
                                description="Show the generic manual phone-system forwarding setup UI in organization settings. Turning this on also enables Per-number Call Handling and Multi-Line Routing."
                                featureKey="generic_phone_system_forwarding_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Call Routing Workflow Editor"
                                description="Allow editing transfer options and routing conditions directly on the call-routing Workflow canvas."
                                featureKey="call_routing_workflow_editor_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Match tags into routing options"
                                description="Enable client-tag matching for inbound voice transfer options. When on, Voice settings can map client tags such as SCAN to specific transfer numbers."
                                featureKey="call_routing_client_tag_matching_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Survey Outreach"
                                description="Allow sending survey messages to caregivers and tracking responses."
                                featureKey="survey_outreach_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Caregiver Preference Profiles"
                                description="Enable scheduling preference profiles and caregiver preference survey campaigns over SMS and voice."
                                featureKey="caregiver_preference_profiles_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Onboarding Availability Verification"
                                description="Allow onboarding to text caregivers to confirm or update imported availability and preferences."
                                featureKey="onboarding_availability_verification_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Auto-Send Contact Card on New Caregiver"
                                description="Automatically text the onboarding contact card to caregivers newly added in the EHR (only those with a phone). Off by default."
                                featureKey="auto_send_contact_card_on_new_caregiver"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Tag Matching Overrides"
                                description="Coordinators can override Phoebe's canonical tag-matching for one client (in the Client Detail panel) or for the whole agency (in Settings → Tag Matching). Also gates sync-time LLM classification of new EHR tags."
                                featureKey="tag_overrides_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Phoebe end-to-end outreach"
                                description="Adds the Phoebe settings tab for configuring Phoebe's unattended scheduling automations."
                                featureKey="phoebe_end_to_end_outreach_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Caregiver Engagement Texts"
                                description="Show the Caregiver engagement tab in redesigned settings and allow caregiver-facing birthday, monthly check-in, and caregiver/client milestone text automations."
                                featureKey="caregiver_engagement_texts_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Coverage Dashboard"
                                description="Give organization members access to Coverage: caregiver availability, coverage rollups, and the map with caregivers and clients across all offices. No separate client-location permission is required."
                                featureKey="coverage_dashboard_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Activity Feed Redesign"
                                description="Replace the classic Activity inbox with the redesigned list and board views for this organization. Rollout gate only."
                                featureKey="activity_feed_redesign_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="UI v3 Components"
                                description="Use the v3 UI components: redesigned list pages with shared headers, tabs, filters, inbox rows, and selection controls. Off by default."
                                featureKey="ui_v3_components"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Voice Calls Table View"
                                description="Show the dense Podium-style table layout on the Voice Calls page with a List | Table switcher, defaulting to Table. When off, the org sees only the classic two-pane list. Rollout gate only."
                                featureKey="voice_calls_table_view_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Voice Call Assignment"
                                description="Let operators assign a voice call to a teammate, creating a pre-assigned activity-feed follow-up item."
                                featureKey="voice_call_assignment_enabled"
                                organization={organization}
                            />
                            <FeatureFlagToggle
                                label="Conversation Ownership"
                                description="Enable conversation assignment and open/closed lifecycle in the inbox: Assigned to me / Unassigned / All queues, assignee picker, close/reopen, timeline system events, and the Kanban board view. Rollout gate only."
                                featureKey="conversation_ownership_enabled"
                                organization={organization}
                            />
                            <ActivityFeedSettingsSection
                                organization={organization}
                            />
                        </div>
                    </CardContent>
                </Card>
                <div className="mt-6">
                    <OrganizationVoiceQualityModeCard
                        organization={organization}
                    />
                    <div className="mt-6">
                        <OrganizationVoiceSettingsCard
                            organizationId={organizationId}
                            voiceSettings={voiceSettings}
                            isLoading={isLoadingVoiceSettings}
                            mutateVoiceSettings={mutateVoiceSettings}
                        />
                    </div>
                    <div className="mt-6">
                        <CallRoutingWorkflowPreview
                            organizationId={organizationId}
                            canEdit={true}
                        />
                    </div>
                </div>
                <div className="mt-6">
                    <OrganizationInboundPayerRoutingCard
                        organizationId={organizationId}
                    />
                </div>
            </div>
            <div>
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Settings className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">
                            Open Shift Settings
                        </h2>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsCalloutJsonModalOpen(true)}
                    >
                        Edit JSON
                    </Button>
                </div>
                <Card>
                    <CardContent className="p-6">
                        {organization.callout_settings ? (
                            <div className="space-y-4 text-sm">
                                <div>
                                    <h4 className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                                        Options
                                    </h4>
                                    <ul className="list-disc space-y-1 pl-5">
                                        {organization.callout_settings.options.map(
                                            (opt) => (
                                                <li key={opt}>{opt}</li>
                                            ),
                                        )}
                                    </ul>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">
                                        Allow Custom:
                                    </span>{' '}
                                    {organization.callout_settings.allow_custom
                                        ? 'Yes'
                                        : 'No'}
                                </div>
                                <div className="grid gap-3 md:grid-cols-2">
                                    <div>
                                        <h4 className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                                            Fill Template (Assigned, Open Shift)
                                        </h4>
                                        <p className="bg-muted rounded border-l-4 border-green-500 p-3 whitespace-pre-wrap">
                                            {
                                                organization.callout_settings
                                                    .fill_shift_template_multi_assigned
                                            }
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                                            Fill Template (Assigned, Recurring
                                            Shift)
                                        </h4>
                                        <p className="bg-muted rounded border-l-4 border-green-500 p-3 whitespace-pre-wrap">
                                            {
                                                organization.callout_settings
                                                    .fill_shift_template_recurring_assigned
                                            }
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                                            Fill Template (Not Assigned)
                                        </h4>
                                        <p className="bg-muted rounded border-l-4 border-gray-500 p-3 whitespace-pre-wrap">
                                            {
                                                organization.callout_settings
                                                    .fill_shift_template_multi_not_assigned
                                            }
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                                            Shift List Item Format (Open Shift)
                                        </h4>
                                        <p className="bg-muted rounded border-l-4 border-gray-500 p-3 whitespace-pre-wrap">
                                            {
                                                organization.callout_settings
                                                    .fill_shift_item_template
                                            }
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                                            Shift List Item Format (Recurring
                                            Shift)
                                        </h4>
                                        <p className="bg-muted rounded border-l-4 border-gray-500 p-3 whitespace-pre-wrap">
                                            {
                                                organization.callout_settings
                                                    .fill_shift_item_template_recurring
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="py-4 text-center">
                                <p className="text-muted-foreground">
                                    Loading open shift settings…
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            <div>
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Settings className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">
                            General Settings
                        </h2>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsGeneralJsonModalOpen(true)}
                    >
                        Edit JSON
                    </Button>
                </div>
                <Card>
                    <CardContent className="p-6">
                        {organization.general_settings ? (
                            <div className="space-y-4 text-sm">
                                <div className="border-border flex flex-wrap items-start justify-between gap-4 rounded-md border p-4">
                                    <div className="space-y-1">
                                        <Label
                                            htmlFor="organization-assistant-name"
                                            className="text-base font-medium"
                                        >
                                            Assistant Name
                                        </Label>
                                        <p className="text-muted-foreground text-sm">
                                            Shared caregiver and client-facing
                                            assistant name used in voice and
                                            SMS. Leave blank to keep Phoebe.
                                        </p>
                                    </div>
                                    <div className="w-full max-w-sm space-y-2">
                                        <Input
                                            id="organization-assistant-name"
                                            placeholder="Phoebe"
                                            value={assistantName}
                                            onChange={(event) =>
                                                setAssistantName(
                                                    event.target.value,
                                                )
                                            }
                                        />
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-muted-foreground text-xs">
                                                Voice previews use{' '}
                                                <span className="font-medium">
                                                    {resolvedAssistantName}
                                                </span>
                                                .
                                            </p>
                                            <Button
                                                size="sm"
                                                onClick={
                                                    handleSaveAssistantName
                                                }
                                                disabled={
                                                    isSavingAssistantName ||
                                                    !isAssistantNameDirty
                                                }
                                            >
                                                {isSavingAssistantName ? (
                                                    <span className="flex items-center gap-2">
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                        Saving…
                                                    </span>
                                                ) : (
                                                    'Save'
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                                <GeneralSettingSwitchRow
                                    label="Display caregiver and client names as Last, First"
                                    description="Controls how caregiver and client names appear throughout the app."
                                    checked={
                                        organization.general_settings
                                            .display_caregiver_names_last_first
                                    }
                                    onCheckedChange={
                                        handleToggleCaregiverNameFormat
                                    }
                                    disabled={isUpdatingCaregiverNameFormat}
                                    ariaLabel="Toggle caregiver display order"
                                />
                                <GeneralSettingSwitchRow
                                    label="Include SMS body in email notifications"
                                    description="When enabled, shift decline emails will include the caregiver’s SMS response message."
                                    checked={
                                        organization.general_settings
                                            .include_sms_body_in_email_notification
                                    }
                                    onCheckedChange={handleToggleSmsBodyInEmail}
                                    disabled={isUpdatingSmsBodyInEmail}
                                    ariaLabel="Toggle SMS body in email notifications"
                                />
                                <GeneralSettingSwitchRow
                                    label="Play call recording notice"
                                    description="When enabled, the assistant includes “on a recorded line” once at the start of caller-facing voice calls."
                                    checked={
                                        organization.general_settings
                                            .call_recording_notice_enabled
                                    }
                                    onCheckedChange={
                                        handleToggleCallRecordingNotice
                                    }
                                    disabled={isUpdatingCallRecordingNotice}
                                    ariaLabel="Toggle call recording notice"
                                />
                                <GeneralSettingSwitchRow
                                    label="Require WellSky client rate to fill"
                                    description="Require a client bill rate when writing assignments to WellSky."
                                    checked={
                                        organization.general_settings
                                            .require_wellsky_client_rate_to_fill
                                    }
                                    onCheckedChange={
                                        handleToggleRequireWellSkyClientRate
                                    }
                                    disabled={
                                        isUpdatingRequireWellSkyClientRate
                                    }
                                    ariaLabel="Toggle require WellSky client rate to fill"
                                />
                                <GeneralSettingSwitchRow
                                    label="Require WellSky caregiver rate to fill"
                                    description="Require a caregiver pay rate when writing assignments to WellSky."
                                    checked={
                                        organization.general_settings
                                            .require_wellsky_caregiver_rate_to_fill
                                    }
                                    onCheckedChange={
                                        handleToggleRequireWellSkyCaregiverRate
                                    }
                                    disabled={
                                        isUpdatingRequireWellSkyCaregiverRate
                                    }
                                    ariaLabel="Toggle require WellSky caregiver rate to fill"
                                />
                                <GeneralSettingSwitchRow
                                    label="AxisCare service rate selection"
                                    description="Show the service rate picker when assigning a caregiver in AxisCare. Off writes the assignment without a service rate, leaving the visit’s existing rate untouched."
                                    checked={
                                        organization.general_settings
                                            .axiscare_service_rate_selection_enabled
                                    }
                                    onCheckedChange={
                                        handleToggleAxisCareServiceRateSelection
                                    }
                                    disabled={
                                        isUpdatingAxisCareServiceRateSelection
                                    }
                                    ariaLabel="Toggle AxisCare service rate selection"
                                />
                                <div className="border-border flex flex-wrap items-center justify-between gap-4 rounded-md border p-4">
                                    <div className="space-y-1">
                                        <p className="text-base font-medium">
                                            Client Reference Term
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            How the assistant refers to the
                                            client in any communication.
                                        </p>
                                    </div>
                                    <Select
                                        value={
                                            organization.general_settings
                                                .client_reference_term
                                        }
                                        onValueChange={
                                            handleUpdateClientReferenceTerm
                                        }
                                        disabled={isUpdatingClientReferenceTerm}
                                    >
                                        <SelectTrigger
                                            id="client-reference-term"
                                            className="w-full"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {['patient', 'client'].map(
                                                (term) => (
                                                    <SelectItem
                                                        key={term}
                                                        value={term}
                                                    >
                                                        {term}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="border-border flex flex-wrap items-center justify-between gap-4 rounded-md border p-4">
                                    <div className="space-y-1">
                                        <p className="text-base font-medium">
                                            Distance Calculation Mode
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            How caregiver-to-client distances
                                            are measured in recommendations.
                                            When Amazon Location Driving is on,
                                            Amazon road routes override this
                                            setting. Otherwise, Straight-line is
                                            instant and Driving uses cached
                                            Mapbox road distance.
                                        </p>
                                    </div>
                                    <Select
                                        value={
                                            organization.general_settings
                                                .distance_calculation_mode
                                        }
                                        onValueChange={
                                            handleUpdateDistanceCalculationMode
                                        }
                                        disabled={
                                            isUpdatingDistanceCalculationMode
                                        }
                                    >
                                        <SelectTrigger
                                            id="distance-calculation-mode"
                                            className="w-full max-w-sm"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="straight_line">
                                                Straight-line (Haversine)
                                            </SelectItem>
                                            <SelectItem value="driving">
                                                Driving (Mapbox road distance)
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="border-border flex flex-wrap items-center justify-between gap-4 rounded-md border p-4">
                                    <div className="space-y-1">
                                        <p className="text-base font-medium">
                                            Sidebar Agent Model
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            Default model for the sidebar agent,
                                            covering outreach chats and the
                                            general (v3) chat. A per-chat pin
                                            overrides it. Leave unset to use the
                                            default.
                                        </p>
                                    </div>
                                    <Select
                                        value={
                                            organization.general_settings
                                                .sidebar_outreach_agent_model ??
                                            SIDEBAR_AGENT_MODEL_UNSET_VALUE
                                        }
                                        onValueChange={(value) =>
                                            handleUpdateSidebarOutreachAgentModel(
                                                value ===
                                                    SIDEBAR_AGENT_MODEL_UNSET_VALUE
                                                    ? null
                                                    : value,
                                            )
                                        }
                                        disabled={
                                            isUpdatingSidebarOutreachAgentModel
                                        }
                                    >
                                        <SelectTrigger
                                            id="sidebar-outreach-agent-model"
                                            className="w-full max-w-sm"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                value={
                                                    SIDEBAR_AGENT_MODEL_UNSET_VALUE
                                                }
                                            >
                                                — no override (use default) —
                                            </SelectItem>
                                            {SUPPORTED_SIDEBAR_OUTREACH_AGENT_MODELS.map(
                                                (model) => (
                                                    <SelectItem
                                                        key={model}
                                                        value={model}
                                                    >
                                                        {model}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="border-border flex flex-wrap items-center justify-between gap-4 rounded-md border p-4">
                                    <div className="space-y-1">
                                        <p className="text-base font-medium">
                                            Sidebar Agent Effort
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            Reasoning effort for the sidebar
                                            agent, covering outreach chats and
                                            the general (v3) chat. Leave unset
                                            to use the default (medium).
                                        </p>
                                    </div>
                                    <Select
                                        value={
                                            organization.general_settings
                                                .sidebar_outreach_agent_effort ??
                                            SIDEBAR_AGENT_MODEL_UNSET_VALUE
                                        }
                                        onValueChange={(value) =>
                                            handleUpdateSidebarOutreachAgentEffort(
                                                value ===
                                                    SIDEBAR_AGENT_MODEL_UNSET_VALUE
                                                    ? null
                                                    : value,
                                            )
                                        }
                                        disabled={
                                            isUpdatingSidebarOutreachAgentEffort
                                        }
                                    >
                                        <SelectTrigger
                                            id="sidebar-outreach-agent-effort"
                                            className="w-full max-w-sm"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem
                                                value={
                                                    SIDEBAR_AGENT_MODEL_UNSET_VALUE
                                                }
                                            >
                                                — no override (use default) —
                                            </SelectItem>
                                            {SUPPORTED_SIDEBAR_OUTREACH_AGENT_EFFORTS.map(
                                                (effort) => (
                                                    <SelectItem
                                                        key={effort}
                                                        value={effort}
                                                    >
                                                        {effort}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <span className="text-muted-foreground">
                                        Primary Data Source:
                                    </span>{' '}
                                    {organization.general_settings
                                        .primary_data_source ?? '—'}
                                </div>
                            </div>
                        ) : (
                            <div className="py-4 text-center">
                                <p className="text-muted-foreground">
                                    Loading general settings…
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            <div>
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Settings className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">
                            Care Plan Settings
                        </h2>
                    </div>
                    {editingEssentialItems !== null && (
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setEditingEssentialItems(null);
                                    setEditingCustomInstructions(null);
                                }}
                                disabled={isSavingCarePlanSettings}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleSaveCarePlanSettings}
                                disabled={isSavingCarePlanSettings}
                            >
                                {isSavingCarePlanSettings && (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Save
                            </Button>
                        </div>
                    )}
                    {editingEssentialItems === null && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setEditingEssentialItems(
                                    carePlanSettings?.essential_items ?? [],
                                );
                                setEditingCustomInstructions(
                                    carePlanSettings?.custom_instructions ?? '',
                                );
                            }}
                        >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                        </Button>
                    )}
                </div>
                <Card>
                    <CardContent className="p-6">
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label className="text-muted-foreground text-sm">
                                    Essential Items (to be included in care plan
                                    summaries)
                                </Label>
                                {editingEssentialItems !== null ? (
                                    <div className="space-y-2">
                                        {editingEssentialItems.map(
                                            (item, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center gap-2"
                                                >
                                                    <span className="text-muted-foreground w-6 text-sm">
                                                        {idx + 1}.
                                                    </span>
                                                    <Input
                                                        value={item}
                                                        onChange={(e) => {
                                                            const updated = [
                                                                ...editingEssentialItems,
                                                            ];
                                                            updated[idx] =
                                                                e.target.value;
                                                            setEditingEssentialItems(
                                                                updated,
                                                            );
                                                        }}
                                                        className="flex-1"
                                                    />
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                            setEditingEssentialItems(
                                                                editingEssentialItems.filter(
                                                                    (_, i) =>
                                                                        i !==
                                                                        idx,
                                                                ),
                                                            )
                                                        }
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500 dark:text-red-400" />
                                                    </Button>
                                                </div>
                                            ),
                                        )}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                setEditingEssentialItems([
                                                    ...editingEssentialItems,
                                                    '',
                                                ])
                                            }
                                        >
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add Item
                                        </Button>
                                    </div>
                                ) : carePlanSettings?.essential_items ? (
                                    <ol className="list-inside list-decimal space-y-1 text-sm">
                                        {carePlanSettings.essential_items.map(
                                            (item, idx) => (
                                                <li key={idx}>{item}</li>
                                            ),
                                        )}
                                    </ol>
                                ) : (
                                    <p className="text-muted-foreground text-sm">
                                        Loading care plan settings…
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label className="text-muted-foreground text-sm">
                                    Custom Instructions
                                </Label>
                                <p className="text-muted-foreground text-xs">
                                    Free-text guidance for what to include or
                                    exclude from care plan summaries (e.g., “Do
                                    not include client personality preferences
                                    or offensive content”).
                                </p>
                                {editingCustomInstructions !== null ? (
                                    <Textarea
                                        value={editingCustomInstructions}
                                        onChange={(e) =>
                                            setEditingCustomInstructions(
                                                e.target.value,
                                            )
                                        }
                                        rows={3}
                                        placeholder="Enter custom instructions for care plan summarization…"
                                    />
                                ) : (
                                    <p className="text-sm">
                                        {carePlanSettings?.custom_instructions || (
                                            <span className="text-muted-foreground italic">
                                                None
                                            </span>
                                        )}
                                    </p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            <div>
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Settings className="text-muted-foreground/70 h-5 w-5" />
                        <h2 className="text-lg font-semibold">
                            Shift Confirmation Settings
                        </h2>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsShiftJsonModalOpen(true)}
                    >
                        Edit JSON
                    </Button>
                </div>
            </div>
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <Users className="text-muted-foreground/70 h-5 w-5" />
                    <h2 className="text-lg font-semibold">
                        Organization Members
                    </h2>
                    {members && (
                        <Badge variant="secondary" className="ml-2">
                            {members.length}{' '}
                            {members.length === 1 ? 'member' : 'members'}
                        </Badge>
                    )}
                </div>
                <Card className="p-0">
                    {isLoadingMembers ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading members…</span>
                            </div>
                        </div>
                    ) : members && members.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Organization role</TableHead>
                                    <TableHead>Member Since</TableHead>
                                    {canRemoveMembers && (
                                        <TableHead className="text-right">
                                            Actions
                                        </TableHead>
                                    )}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {members.map((member) => (
                                    <TableRow key={member.user.id}>
                                        <TableCell className="font-medium">
                                            {member.user.email}
                                        </TableCell>
                                        <TableCell>
                                            {member.title || (
                                                <span className="text-muted-foreground">
                                                    No title
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {member.role ? (
                                                <Badge variant="secondary">
                                                    {orgMemberRoleLabel(
                                                        member.role,
                                                    )}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    No role
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {member.user.created_at
                                                ? new Date(
                                                      member.user.created_at,
                                                  ).toLocaleDateString(
                                                      'en-US',
                                                      {
                                                          year: 'numeric',
                                                          month: 'short',
                                                          day: 'numeric',
                                                      },
                                                  )
                                                : '-'}
                                        </TableCell>
                                        {canRemoveMembers && (
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        setEditingMember(member)
                                                    }
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    disabled={
                                                        removingMemberId ===
                                                        member.user.id
                                                    }
                                                    onClick={async () => {
                                                        if (
                                                            !organizationId ||
                                                            !confirm(
                                                                `Remove ${member.user.email} from this organization?`,
                                                            )
                                                        ) {
                                                            return;
                                                        }

                                                        setRemovingMemberId(
                                                            member.user.id,
                                                        );
                                                        try {
                                                            await getApiClient().delete(
                                                                {
                                                                    url: '/admin/organizations/{organization_id}/members/{user_id}',
                                                                    pathParams:
                                                                        {
                                                                            organization_id:
                                                                                organizationId,
                                                                            user_id:
                                                                                member
                                                                                    .user
                                                                                    .id,
                                                                        },
                                                                },
                                                            );
                                                            toast.success(
                                                                'Member removed from organization',
                                                                {
                                                                    persistNotification: false,
                                                                },
                                                            );
                                                            await mutateMembers();
                                                        } catch {
                                                            toast.error(
                                                                'Failed to remove member',
                                                                {
                                                                    persistNotification: false,
                                                                },
                                                            );
                                                        } finally {
                                                            setRemovingMemberId(
                                                                null,
                                                            );
                                                        }
                                                    }}
                                                >
                                                    {removingMemberId ===
                                                    member.user.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            No members found
                        </div>
                    )}
                </Card>
            </div>
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <Mail className="text-muted-foreground/70 h-5 w-5" />
                    <h2 className="text-lg font-semibold">
                        Pending Invitations
                    </h2>
                    {invitations && (
                        <Badge variant="secondary" className="ml-2">
                            {invitations.length}{' '}
                            {invitations.length === 1
                                ? 'invitation'
                                : 'invitations'}
                        </Badge>
                    )}
                </div>
                <Card className="p-0">
                    {isLoadingInvitations ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading invitations…</span>
                            </div>
                        </div>
                    ) : invitations && invitations.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Organization role</TableHead>
                                    <TableHead>Invited By</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Expires</TableHead>
                                    <TableHead className="text-right">
                                        Actions
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {invitations.map((invitation) => (
                                    <TableRow key={invitation.id}>
                                        <TableCell className="font-medium">
                                            {invitation.email}
                                        </TableCell>
                                        <TableCell>
                                            {invitation.first_name ||
                                            invitation.last_name ? (
                                                `${invitation.first_name ?? ''} ${invitation.last_name ?? ''}`.trim()
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    No name
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {invitation.role ? (
                                                <Badge variant="secondary">
                                                    {orgMemberRoleLabel(
                                                        invitation.role,
                                                    )}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    No role
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {invitation.invited_by_email || (
                                                <span className="text-muted-foreground">
                                                    Unknown
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(
                                                invitation.created_at,
                                            ).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                            })}
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={
                                                    invitation.is_expired
                                                        ? 'text-red-600 dark:text-red-400'
                                                        : ''
                                                }
                                            >
                                                {new Date(
                                                    invitation.expires_at,
                                                ).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                })}
                                                {invitation.is_expired && (
                                                    <Badge
                                                        variant="destructive"
                                                        className="ml-2"
                                                    >
                                                        Expired
                                                    </Badge>
                                                )}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                title="Add to another organization"
                                                onClick={() =>
                                                    setAddToOrgInvitation({
                                                        email: invitation.email,
                                                        first_name:
                                                            invitation.first_name ??
                                                            null,
                                                        last_name:
                                                            invitation.last_name ??
                                                            null,
                                                        role:
                                                            invitation.role ??
                                                            null,
                                                    })
                                                }
                                            >
                                                <Building2 className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                disabled={
                                                    deletingInvitationId ===
                                                    invitation.id
                                                }
                                                onClick={async () => {
                                                    if (
                                                        !organizationId ||
                                                        !confirm(
                                                            `Delete invitation for ${invitation.email}?`,
                                                        )
                                                    ) {
                                                        return;
                                                    }

                                                    setDeletingInvitationId(
                                                        invitation.id,
                                                    );
                                                    try {
                                                        await getApiClient().delete(
                                                            {
                                                                url: '/admin/organizations/{organization_id}/invitations/{invitation_id}',
                                                                pathParams: {
                                                                    organization_id:
                                                                        organizationId,
                                                                    invitation_id:
                                                                        invitation.id,
                                                                },
                                                            },
                                                        );
                                                        toast.success(
                                                            'Invitation deleted',
                                                            {
                                                                persistNotification: false,
                                                            },
                                                        );
                                                        await mutateInvitations();
                                                    } catch {
                                                        toast.error(
                                                            'Failed to delete invitation',
                                                            {
                                                                persistNotification: false,
                                                            },
                                                        );
                                                    } finally {
                                                        setDeletingInvitationId(
                                                            null,
                                                        );
                                                    }
                                                }}
                                            >
                                                {deletingInvitationId ===
                                                invitation.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            No pending invitations
                        </div>
                    )}
                </Card>
                <AddInvitedUserToOrgModal
                    invitation={addToOrgInvitation}
                    onClose={() => setAddToOrgInvitation(null)}
                />
            </div>
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <MessageSquare className="text-muted-foreground/70 h-5 w-5" />
                    <h2 className="text-lg font-semibold">
                        External SMS Provider Accounts
                    </h2>
                    {externalSmsProviderAccounts && (
                        <Badge variant="secondary" className="ml-2">
                            {externalSmsProviderAccounts.length}{' '}
                            {externalSmsProviderAccounts.length === 1
                                ? 'account'
                                : 'accounts'}
                        </Badge>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsSmsModalOpen(true)}
                        className="ml-auto"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Account
                    </Button>
                </div>
                <Card className="p-0">
                    {isLoadingExternalSmsProviderAccounts ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading SMS accounts…</span>
                            </div>
                        </div>
                    ) : externalSmsProviderAccounts &&
                      externalSmsProviderAccounts.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Provider</TableHead>
                                    <TableHead>Display Name</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {externalSmsProviderAccounts.map((account) => (
                                    <TableRow key={account.id}>
                                        <TableCell className="capitalize">
                                            {account.provider}
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {account.display_name}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={
                                                    account.is_active
                                                        ? 'default'
                                                        : 'secondary'
                                                }
                                            >
                                                {account.is_active
                                                    ? 'Active'
                                                    : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(
                                                account.created_at,
                                            ).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setEditingSmsAccount({
                                                            id: account.id,
                                                            data: {
                                                                provider:
                                                                    account.provider,
                                                                displayName:
                                                                    account.display_name,
                                                                isActive:
                                                                    account.is_active,
                                                            },
                                                        });
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={async () => {
                                                        if (
                                                            confirm(
                                                                'Are you sure you want to delete this SMS provider account?',
                                                            )
                                                        ) {
                                                            try {
                                                                await getApiClient().delete(
                                                                    {
                                                                        url: '/admin/organizations/{organization_id}/external-sms-provider-accounts/{account_id}',
                                                                        pathParams:
                                                                            {
                                                                                organization_id:
                                                                                    organizationId!,
                                                                                account_id:
                                                                                    account.id,
                                                                            },
                                                                    },
                                                                );
                                                                toast.success(
                                                                    'SMS provider account deleted',
                                                                    {
                                                                        persistNotification: false,
                                                                    },
                                                                );
                                                                mutateExternalSmsProviderAccounts();
                                                            } catch (error) {
                                                                toast.error(
                                                                    'Failed to delete SMS provider account',
                                                                    {
                                                                        persistNotification: false,
                                                                    },
                                                                );
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            No SMS provider accounts configured
                        </div>
                    )}
                </Card>
            </div>
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <MessageSquare className="text-muted-foreground/70 h-5 w-5" />
                    <h2 className="text-lg font-semibold">Phone Numbers</h2>
                    {phoneNumbers && (
                        <Badge variant="secondary" className="ml-2">
                            {phoneNumbers.length}{' '}
                            {phoneNumbers.length === 1 ? 'number' : 'numbers'}
                        </Badge>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsSmsNumberModalOpen(true)}
                        className="ml-auto"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Phone Number
                    </Button>
                </div>
                <Card className="p-0">
                    {isLoadingPhoneNumbers ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading phone numbers…</span>
                            </div>
                        </div>
                    ) : phoneNumbers && phoneNumbers.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Mode</TableHead>
                                    <TableHead>Phone Number</TableHead>
                                    {phoneSystemMultiLineEnabled && (
                                        <TableHead>Voice Line</TableHead>
                                    )}
                                    <TableHead>Capabilities</TableHead>
                                    <TableHead>SMS Provider</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {[...phoneNumbers]
                                    .sort((a, b) => {
                                        if (a.mode === b.mode) {
                                            return 0;
                                        }
                                        return a.mode === 'live' ? -1 : 1;
                                    })
                                    .map((phoneNumber) => {
                                        return (
                                            <TableRow key={phoneNumber.id}>
                                                <TableCell>
                                                    {phoneNumber.mode ===
                                                    'sandbox' ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="h-2 w-2 rounded-full bg-orange-500" />
                                                            <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
                                                                Test
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <span className="h-2 w-2 rounded-full bg-green-500" />
                                                            <span className="text-sm font-medium text-green-700 dark:text-green-400">
                                                                Live
                                                            </span>
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {phoneNumber.number}
                                                </TableCell>
                                                {phoneSystemMultiLineEnabled && (
                                                    <TableCell>
                                                        {phoneNumber.voice_enabled ? (
                                                            <div className="space-y-1">
                                                                <div className="font-medium">
                                                                    {phoneNumber.display_name ||
                                                                        'Unnamed line'}
                                                                </div>
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="capitalize"
                                                                >
                                                                    {
                                                                        phoneNumber.role
                                                                    }
                                                                </Badge>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted-foreground">
                                                                —
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <div className="flex gap-1">
                                                        {phoneNumber.sms_enabled && (
                                                            <Badge variant="outline">
                                                                SMS
                                                            </Badge>
                                                        )}
                                                        {phoneNumber.voice_enabled && (
                                                            <Badge variant="outline">
                                                                Voice
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="capitalize">
                                                    {phoneNumber.sms_provider || (
                                                        <span className="text-muted-foreground">
                                                            —
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {phoneNumber.external_sms_account_id ? (
                                                        <Badge variant="outline">
                                                            External
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline">
                                                            Managed
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setEditingPhoneNumber(
                                                                    {
                                                                        id: phoneNumber.id,
                                                                        data: {
                                                                            sms_provider:
                                                                                phoneNumber.sms_provider ??
                                                                                null,
                                                                            number: phoneNumber.number,
                                                                            messaging_service_sid:
                                                                                phoneNumber.messaging_service_sid ||
                                                                                undefined,
                                                                            external_sms_account_id:
                                                                                phoneNumber.external_sms_account_id ||
                                                                                undefined,
                                                                            mode: phoneNumber.mode,
                                                                            sms_enabled:
                                                                                phoneNumber.sms_enabled,
                                                                            voice_enabled:
                                                                                phoneNumber.voice_enabled,
                                                                            display_name:
                                                                                phoneNumber.display_name ||
                                                                                undefined,
                                                                            role: phoneNumber.role,
                                                                            default_call_routing_destination_id:
                                                                                phoneNumber.default_call_routing_destination_id ||
                                                                                undefined,
                                                                        },
                                                                    },
                                                                );
                                                            }}
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={async () => {
                                                                if (
                                                                    confirm(
                                                                        'Are you sure you want to delete this phone number?',
                                                                    )
                                                                ) {
                                                                    try {
                                                                        await getApiClient().delete(
                                                                            {
                                                                                url: '/admin/organizations/{organization_id}/phone-numbers/{phone_number_id}',
                                                                                pathParams:
                                                                                    {
                                                                                        organization_id:
                                                                                            organizationId!,
                                                                                        phone_number_id:
                                                                                            phoneNumber.id,
                                                                                    },
                                                                            },
                                                                        );
                                                                        toast.success(
                                                                            'Phone number deleted',
                                                                            {
                                                                                persistNotification: false,
                                                                            },
                                                                        );
                                                                        mutatePhoneNumbers();
                                                                    } catch (error) {
                                                                        toast.error(
                                                                            'Failed to delete phone number',
                                                                            {
                                                                                persistNotification: false,
                                                                            },
                                                                        );
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            No phone numbers configured
                        </div>
                    )}
                </Card>
            </div>
            <div>
                <EhrAccessCard organizationId={organizationId!} />
            </div>
            <div>
                <ExternalPhoneForwardingCard
                    organizationId={organizationId!}
                    phoneNumbers={phoneNumbers}
                />
            </div>
            <div>
                <PhoneSystemForwardingCard
                    organizationId={organizationId!}
                    variant="admin"
                    appearance="classic"
                />
            </div>
            {phoneSystemPortInEnabled ? (
                <div>
                    <PhoneNumberPortInPanel
                        organizationId={organizationId}
                        e911Enabled={phoneSystemE911Enabled}
                    />
                </div>
            ) : null}
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <Key className="text-muted-foreground/70 h-5 w-5" />
                    <h2 className="text-lg font-semibold">
                        Data Source Credentials
                    </h2>
                    {dataSourceAccounts && (
                        <Badge variant="secondary" className="ml-2">
                            {dataSourceAccounts.length}{' '}
                            {dataSourceAccounts.length === 1
                                ? 'credential'
                                : 'credentials'}
                        </Badge>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsDataSourceAccountModalOpen(true)}
                        className="ml-auto"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Add Credentials
                    </Button>
                </div>
                <Card className="p-0">
                    {isLoadingDataSourceAccounts ? (
                        <div className="flex h-32 items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading data source credentials…</span>
                            </div>
                        </div>
                    ) : dataSourceAccounts && dataSourceAccounts.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>System</TableHead>
                                    <TableHead>Base URL</TableHead>
                                    <TableHead>Username</TableHead>
                                    <TableHead>Location IDs</TableHead>
                                    <TableHead>Sync Jobs</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {dataSourceAccounts.map((account) => (
                                    <TableRow key={account.id}>
                                        <TableCell className="font-medium uppercase">
                                            {account.type}
                                        </TableCell>
                                        <TableCell>
                                            {account.base_url}
                                        </TableCell>
                                        <TableCell>
                                            {account.username}
                                        </TableCell>
                                        <TableCell>
                                            {account.location_ids?.join(', ') ??
                                                '-'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1">
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        'w-fit text-xs',
                                                        account.sync_enabled
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                                                            : 'bg-muted text-muted-foreground',
                                                    )}
                                                >
                                                    {account.sync_enabled
                                                        ? 'Enabled'
                                                        : 'Disabled'}
                                                </Badge>
                                                <span className="text-muted-foreground text-xs">
                                                    Failures:{' '}
                                                    {account.sync_failure_count}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {new Date(
                                                account.created_at,
                                            ).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setEditingDataSourceAccount(
                                                            {
                                                                id: account.id,
                                                                data: buildDataSourceAccountModalInitialData(
                                                                    account,
                                                                ),
                                                            },
                                                        );
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                {account.type ===
                                                    'generations_homecare' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setGenerationsPortalLoginsAccountId(
                                                                account.id,
                                                            );
                                                        }}
                                                    >
                                                        <KeyRound className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={async () => {
                                                        if (
                                                            confirm(
                                                                'Are you sure you want to delete these integration credentials?',
                                                            )
                                                        ) {
                                                            try {
                                                                await getApiClient().delete(
                                                                    {
                                                                        url: '/admin/organizations/{organization_id}/data-source-accounts/{account_id}',
                                                                        pathParams:
                                                                            {
                                                                                organization_id:
                                                                                    organizationId!,
                                                                                account_id:
                                                                                    account.id,
                                                                            },
                                                                    },
                                                                );
                                                                toast.success(
                                                                    'Data source credentials deleted',
                                                                    {
                                                                        persistNotification: false,
                                                                    },
                                                                );
                                                                mutateDataSourceAccounts();
                                                            } catch (error) {
                                                                toast.error(
                                                                    'Failed to delete data source credentials',
                                                                    {
                                                                        persistNotification: false,
                                                                    },
                                                                );
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center">
                            No data source credentials configured
                        </div>
                    )}
                </Card>
            </div>
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <Settings className="text-muted-foreground/70 h-5 w-5" />
                    <h2 className="text-lg font-semibold">
                        Organization Settings
                    </h2>
                </div>
                <Card className="p-0">
                    <CardContent className="p-6">
                        {organization.shift_confirmation_settings ||
                        organization.callout_settings ||
                        organization.general_settings ? (
                            <pre className="bg-muted overflow-x-auto rounded-md p-4">
                                <code className="text-sm">
                                    {JSON.stringify(
                                        {
                                            shift_confirmation:
                                                organization.shift_confirmation_settings,
                                            callout:
                                                organization.callout_settings,
                                            general:
                                                organization.general_settings,
                                        },
                                        null,
                                        2,
                                    )}
                                </code>
                            </pre>
                        ) : (
                            <div className="text-muted-foreground py-8 text-center">
                                No settings configured
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
            {organization && (
                <OrganizationFormModal
                    key={organizationId}
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    mode="edit"
                    organizationId={organizationId}
                    initialData={buildOrganizationFormModalInitialData(
                        organization,
                    )}
                />
            )}
            {organizationId && (
                <React.Fragment>
                    <A2pComplianceProfileModal
                        open={isA2pModalOpen}
                        onOpenChange={setIsA2pModalOpen}
                        a2pComplianceProfile={a2pComplianceProfile}
                        isSaving={isSavingA2p}
                        onSave={async (values) => {
                            if (!organizationId) {
                                return;
                            }
                            setIsSavingA2p(true);
                            try {
                                await getApiClient().put({
                                    url: '/admin/organizations/{organization_id}/a2p-compliance-profile',
                                    pathParams: {
                                        organization_id: organizationId,
                                    },
                                    body: values,
                                });
                                toast.success('A2P profile saved', {
                                    persistNotification: false,
                                });
                                await mutateA2pComplianceProfile();
                            } catch (e) {
                                toast.error('Failed to save A2P profile', {
                                    persistNotification: false,
                                });
                            } finally {
                                setIsSavingA2p(false);
                            }
                        }}
                    />
                    <ExternalSmsProviderAccountModal
                        isOpen={isSmsModalOpen}
                        onClose={() => setIsSmsModalOpen(false)}
                        mode="create"
                        organizationId={organizationId}
                        mutate={mutateExternalSmsProviderAccounts}
                    />
                    {editingSmsAccount && (
                        <ExternalSmsProviderAccountModal
                            isOpen={!!editingSmsAccount}
                            onClose={() => setEditingSmsAccount(null)}
                            mode="edit"
                            organizationId={organizationId}
                            accountId={editingSmsAccount.id}
                            initialData={editingSmsAccount.data}
                            mutate={mutateExternalSmsProviderAccounts}
                        />
                    )}
                    <DataSourceAccountModal
                        isOpen={isDataSourceAccountModalOpen}
                        onClose={() => setIsDataSourceAccountModalOpen(false)}
                        mode="create"
                        organizationId={organizationId}
                        existingDataSourceCount={dataSourceAccounts?.length}
                        currentPrimaryDataSource={
                            organization?.general_settings
                                ?.primary_data_source ?? null
                        }
                        onGeneralSettingsChange={mutateOrganization}
                        mutate={mutateDataSourceAccounts}
                    />
                    {editingDataSourceAccount && (
                        <DataSourceAccountModal
                            isOpen={!!editingDataSourceAccount}
                            onClose={() => setEditingDataSourceAccount(null)}
                            mode="edit"
                            organizationId={organizationId}
                            credentialsId={editingDataSourceAccount.id}
                            initialData={editingDataSourceAccount.data}
                            existingDataSourceCount={dataSourceAccounts?.length}
                            currentPrimaryDataSource={
                                organization?.general_settings
                                    ?.primary_data_source ?? null
                            }
                            onGeneralSettingsChange={mutateOrganization}
                            mutate={mutateDataSourceAccounts}
                        />
                    )}
                    {generationsPortalLoginsAccountId && organizationId && (
                        <GenerationsPortalLoginsModal
                            isOpen={!!generationsPortalLoginsAccountId}
                            onClose={() => {
                                setGenerationsPortalLoginsAccountId(null);
                            }}
                            organizationId={organizationId}
                            accountId={generationsPortalLoginsAccountId}
                        />
                    )}
                    <PhoneNumberModal
                        isOpen={isSmsNumberModalOpen}
                        onClose={() => setIsSmsNumberModalOpen(false)}
                        mode="create"
                        organizationId={organizationId}
                        mutate={mutatePhoneNumbers}
                        externalAccounts={externalSmsProviderAccounts}
                        multiLineRoutingEnabled={phoneSystemMultiLineEnabled}
                        e911Enabled={phoneSystemE911Enabled}
                    />
                    {editingPhoneNumber && (
                        <PhoneNumberModal
                            isOpen={!!editingPhoneNumber}
                            onClose={() => setEditingPhoneNumber(null)}
                            mode="edit"
                            organizationId={organizationId}
                            phoneNumberId={editingPhoneNumber.id}
                            initialData={editingPhoneNumber.data}
                            mutate={mutatePhoneNumbers}
                            externalAccounts={externalSmsProviderAccounts}
                            multiLineRoutingEnabled={
                                phoneSystemMultiLineEnabled
                            }
                            e911Enabled={phoneSystemE911Enabled}
                        />
                    )}
                    <OrganizationMemberEditModal
                        isOpen={editingMember !== null}
                        onClose={() => setEditingMember(null)}
                        organizationId={organizationId}
                        member={editingMember}

                        mutateMembers={mutateMembers}
                    />
                    <ShiftConfirmationSettingsJsonModal
                        isOpen={isShiftJsonModalOpen}
                        onClose={() => setIsShiftJsonModalOpen(false)}
                        organizationId={organizationId}
                        initialValue={shiftConfirmationSettings}
                        onMutate={() => {
                            mutateShiftConfirmationSettings();
                            mutateOrganization();
                        }}
                    />
                    <CalloutSettingsJsonModal
                        isOpen={isCalloutJsonModalOpen}
                        onClose={() => setIsCalloutJsonModalOpen(false)}
                        organizationId={organizationId}
                        initialValue={organization.callout_settings}
                        onMutate={() => {
                            mutateOrganization();
                        }}
                    />
                    <GeneralSettingsJsonModal
                        isOpen={isGeneralJsonModalOpen}
                        onClose={() => setIsGeneralJsonModalOpen(false)}
                        organizationId={organizationId}
                        initialValue={organization.general_settings}
                        onMutate={() => {
                            mutateOrganization();
                        }}
                    />
                </React.Fragment>
            )}
        </div>
    );
}
function A2pComplianceProfilePanel({
    a2pComplianceProfile,
    isLoading,
    isDeleting,
    isRegenerating,
    onOpenModal,
    onRegenerate,
    onDelete,
}: {
    a2pComplianceProfile: A2pComplianceProfile | undefined;
    isLoading: boolean;
    isDeleting: boolean;
    isRegenerating: boolean;
    onOpenModal: () => void;
    onRegenerate: () => Promise<void>;
    onDelete: () => Promise<void>;
}) {
    if (isLoading) {
        return (
            <div className="text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading A2P profile…</span>
            </div>
        );
    }

    return (
        <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
                {a2pComplianceProfile ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Badge variant="default">Configured</Badge>
                        </div>
                        <div className="space-y-1">
                            <p className="font-medium">
                                {a2pComplianceProfile.legal_business_name}
                            </p>
                            <p className="text-muted-foreground text-sm">
                                {a2pComplianceProfile.city},{' '}
                                {a2pComplianceProfile.state_province}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <Badge variant="outline">Not Configured</Badge>
                        <p className="text-muted-foreground text-sm">
                            No A2P compliance profile has been set up for this
                            organization
                        </p>
                    </div>
                )}
            </div>
            <div className="flex shrink-0 gap-2">
                <Button variant="outline" size="sm" onClick={onOpenModal}>
                    {a2pComplianceProfile ? (
                        <React.Fragment>
                            <Edit className="mr-2 h-4 w-4" /> Edit
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Plus className="mr-2 h-4 w-4" /> Create
                        </React.Fragment>
                    )}
                </Button>
                {a2pComplianceProfile && (
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={isRegenerating}
                        onClick={onRegenerate}
                        title="Regenerate the Terms of Service and Privacy Policy while keeping the opt-in form URL"
                    >
                        {isRegenerating && <Spinner className="mr-2" />}
                        Regenerate docs
                    </Button>
                )}
                {a2pComplianceProfile && (
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={isDeleting}
                        onClick={onDelete}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

function TwilioA2pRegistrationPanel({
    a2pComplianceProfile,
    twilioA2pRegistration,
    isLoading,
    isSaving,
    isStarting,
    onSave,
    onStart,
    isPurging,
    onPurge,
    isSendingTestText,
    onSendTestText,
    onRefreshRegistration,
}: {
    a2pComplianceProfile: A2pComplianceProfile | undefined;
    twilioA2pRegistration: TwilioA2pRegistrationDetail | undefined;
    isLoading: boolean;
    isSaving: boolean;
    isStarting: boolean;
    onSave: (values: TwilioA2pRegistrationUpdate) => Promise<void>;
    onStart: () => Promise<void>;
    isPurging: boolean;
    onPurge: () => Promise<void>;
    isSendingTestText: boolean;
    onSendTestText: () => Promise<void>;
    onRefreshRegistration: () => Promise<void>;
}) {
    const isDev = getRunEnvironment() === 'development';
    const [registrationMode, setRegistrationMode] = useState<
        TwilioA2pRegistrationUpdate['registration_mode']
    >(twilioA2pRegistration?.registration_mode ?? 'auto');
    const [areaCode, setAreaCode] = useState<string>(
        twilioA2pRegistration?.desired_area_code ?? '',
    );
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [isStartConfirmationOpen, setIsStartConfirmationOpen] =
        useState(false);

    useEffect(() => {
        if (twilioA2pRegistration) {
            setRegistrationMode(twilioA2pRegistration.registration_mode);
            setAreaCode(twilioA2pRegistration.desired_area_code ?? '');
        }
    }, [twilioA2pRegistration]);

    if (isLoading) {
        return (
            <div className="text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading registration…</span>
            </div>
        );
    }

    const canStart = !!a2pComplianceProfile && registrationMode === 'auto';
    const hasStarted = !!twilioA2pRegistration?.customer_profile_sid;
    const handleConfirmStart = async () => {
        setIsStartConfirmationOpen(false);
        await onStart();
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
                <div className="flex items-center gap-3">
                    <div className="text-muted-foreground text-sm">Mode</div>
                    <Select
                        value={registrationMode ?? 'auto'}
                        onValueChange={(v) =>
                            setRegistrationMode(v as 'auto' | 'manual')
                        }
                    >
                        <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="auto">Auto</SelectItem>
                            <SelectItem value="manual">Manual</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="bg-border h-6 w-px" />
                <div className="flex items-center gap-3">
                    <div className="text-muted-foreground text-sm">
                        Desired Area Code
                    </div>
                    <Input
                        className="w-32"
                        placeholder="e.g., 303"
                        value={areaCode}
                        onChange={(e) => setAreaCode(e.target.value)}
                    />
                </div>
                <div className="ml-auto flex gap-2">
                    <Button
                        variant="outline"
                        disabled={isSaving}
                        onClick={() =>
                            onSave({
                                registration_mode: registrationMode,
                                desired_area_code: areaCode || null,
                            })
                        }
                    >
                        {isSaving && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Save
                    </Button>
                    <Button
                        disabled={!canStart || isStarting}
                        onClick={() => setIsStartConfirmationOpen(true)}
                    >
                        {isStarting && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {hasStarted
                            ? 'Retrigger Provisioning'
                            : 'Start Provisioning'}
                    </Button>
                    {isDev && (
                        <Button
                            variant="destructive"
                            disabled={isPurging}
                            onClick={onPurge}
                            title="Delete Twilio resources and reset registration (dev only)"
                        >
                            {isPurging && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Reset (Dev)
                        </Button>
                    )}
                </div>
            </div>
            <Dialog
                open={isStartConfirmationOpen}
                onOpenChange={setIsStartConfirmationOpen}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Start Twilio provisioning?</DialogTitle>
                        <DialogDescription>
                            Before starting, confirm that you’ve made a
                            screenshot of the SMS Opt-In page and placed it in
                            the Opt-In Form document.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={isStarting}
                            onClick={() => setIsStartConfirmationOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={isStarting}
                            onClick={() => {
                                void handleConfirmStart();
                            }}
                        >
                            {isStarting && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Yes, start provisioning
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {!a2pComplianceProfile && (
                <div className="text-muted-foreground text-xs">
                    Create an A2P compliance profile before starting
                    provisioning.
                </div>
            )}
            {twilioA2pRegistration && (
                <TextingDeliveryTestSection
                    canary={twilioA2pRegistration.latest_canary ?? null}
                    isSending={isSendingTestText}
                    onSend={onSendTestText}
                    onRefresh={onRefreshRegistration}
                />
            )}
            {twilioA2pRegistration && (
                <Collapsible
                    open={isDetailsOpen}
                    onOpenChange={setIsDetailsOpen}
                >
                    <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm">
                        <ChevronDown
                            className={cn(
                                'h-4 w-4 transition-transform',
                                !isDetailsOpen && '-rotate-90',
                            )}
                        />
                        Provisioning Details
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <TwilioProvisioningDetails
                            registration={twilioA2pRegistration}
                        />
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
}

function serverErrorDetail(error: unknown): string | null {
    if (!(error instanceof ApiError)) {
        return null;
    }
    const body = error.body;
    if (body === null || typeof body !== 'object' || !('detail' in body)) {
        return null;
    }
    const detail = (body as { readonly detail: unknown }).detail;
    return typeof detail === 'string' ? detail : null;
}

// Human explanations for the Twilio error codes the delivery test hits in
// practice. Anything else falls back to the linked Twilio docs page.
const TWILIO_ERROR_HINTS: Record<number, string> = {
    30007: 'The carrier filtered the message.',
    30032: 'The toll-free number is not verified for messaging.',
    30034: 'Carriers do not recognize this number as A2P-registered.',
};

function TextingDeliveryTestSection({
    canary,
    isSending,
    onSend,
    onRefresh,
}: {
    canary: TextingCanaryAttempt | null;
    isSending: boolean;
    onSend: () => Promise<void>;
    onRefresh: () => Promise<void>;
}) {
    const when =
        canary === null
            ? null
            : formatDistanceToNowStrict(new Date(canary.created_at), {
                  addSuffix: true,
              });
    return (
        <div className="flex flex-col gap-2 border-t pt-4">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm font-medium">Delivery test</div>
                    <p className="text-muted-foreground text-xs">
                        Sends one real text from the org’s onboarding number to
                        a Phoebe-owned verification number and tracks its
                        delivery. Fires automatically 10 minutes after the
                        number registers.
                    </p>
                </div>
                <div className="flex gap-2">
                    {canary?.outcome === 'pending' && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void onRefresh()}
                        >
                            Refresh
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        disabled={isSending || canary?.outcome === 'pending'}
                        onClick={() => void onSend()}
                    >
                        {isSending ? (
                            <Spinner className="size-4" />
                        ) : (
                            <Send className="size-4" />
                        )}
                        Send test text
                    </Button>
                </div>
            </div>
            {canary === null ? (
                <p className="text-muted-foreground text-sm">
                    No delivery test has run yet.
                </p>
            ) : canary.outcome === 'delivered' ? (
                <p className="text-sm">
                    <Badge variant="success" className="mr-2 gap-1">
                        <CircleCheck />
                        <span>Test text delivered</span>
                    </Badge>
                    <span className="text-muted-foreground">
                        {when} from {canary.from_number}
                    </span>
                </p>
            ) : canary.outcome === 'pending' ? (
                <p className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Spinner className="size-4" />
                    Test text sent {when}. Waiting for delivery confirmation
                    from Twilio — refresh to update.
                </p>
            ) : (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <span className="font-medium">Test text failed</span>{' '}
                    <span>
                        {when} from {canary.from_number}.
                    </span>{' '}
                    {canary.error_code !== null ? (
                        <span>
                            Twilio error {canary.error_code}
                            {TWILIO_ERROR_HINTS[canary.error_code]
                                ? `: ${TWILIO_ERROR_HINTS[canary.error_code]}`
                                : '.'}{' '}
                            <a
                                className="underline"
                                href={`https://www.twilio.com/docs/errors/${canary.error_code}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Twilio docs
                            </a>
                        </span>
                    ) : canary.provider_status === 'send_error' ? (
                        <span>
                            The send failed before reaching Twilio — check the
                            API logs.
                        </span>
                    ) : (
                        <span>Provider status: {canary.provider_status}.</span>
                    )}
                </div>
            )}
        </div>
    );
}

function TwilioProvisioningDetails({
    registration,
}: {
    registration: TwilioA2pRegistration;
}) {
    const sections = [
        {
            title: '1. Customer Profile',
            fields: [
                {
                    label: 'customer_profile_sid',
                    value: registration.customer_profile_sid,
                },
                {
                    label: 'business_end_user_sid',
                    value: registration.business_end_user_sid,
                },
                {
                    label: 'business_end_user_to_customer_profile_assignment_sid',
                    value: registration.business_end_user_to_customer_profile_assignment_sid,
                },
                {
                    label: 'authorized_representative_1_end_user_sid',
                    value: registration.authorized_representative_1_end_user_sid,
                },
                {
                    label: 'authorized_representative_to_customer_profile_assignment_sid',
                    value: registration.authorized_representative_to_customer_profile_assignment_sid,
                },
                { label: 'address_sid', value: registration.address_sid },
                {
                    label: 'supporting_document_sid',
                    value: registration.supporting_document_sid,
                },
                {
                    label: 'supporting_document_to_customer_profile_assignment_sid',
                    value: registration.supporting_document_to_customer_profile_assignment_sid,
                },
                {
                    label: 'secondary_to_primary_customer_profile_assignment_sid',
                    value: registration.secondary_to_primary_customer_profile_assignment_sid,
                },
                {
                    label: 'customer_profile_evaluation_sid',
                    value: registration.customer_profile_evaluation_sid,
                },
                {
                    label: 'customer_profile_approval_status',
                    value: registration.customer_profile_approval_status,
                    isStatus: true,
                },
            ],
        },
        {
            title: '2. A2P Messaging Profile',
            fields: [
                {
                    label: 'a2p_messaging_profile_sid',
                    value: registration.a2p_messaging_profile_sid,
                },
                {
                    label: 'a2p_messaging_profile_end_user_sid',
                    value: registration.a2p_messaging_profile_end_user_sid,
                },
                {
                    label: 'a2p_end_user_to_a2p_messaging_profile_assignment_sid',
                    value: registration.a2p_end_user_to_a2p_messaging_profile_assignment_sid,
                },
                {
                    label: 'customer_profile_to_a2p_messaging_profile_assignment_sid',
                    value: registration.customer_profile_to_a2p_messaging_profile_assignment_sid,
                },
                {
                    label: 'a2p_messaging_profile_evaluation_sid',
                    value: registration.a2p_messaging_profile_evaluation_sid,
                },
                {
                    label: 'a2p_messaging_profile_approval_status',
                    value: registration.a2p_messaging_profile_approval_status,
                    isStatus: true,
                },
            ],
        },
        {
            title: '3. Brand Registration',
            fields: [
                {
                    label: 'brand_registration_sid',
                    value: registration.brand_registration_sid,
                },
                {
                    label: 'brand_registration_approval_status',
                    value: registration.brand_registration_approval_status,
                    isStatus: true,
                },
            ],
        },
        {
            title: '4. Messaging Service',
            fields: [
                {
                    label: 'messaging_service_sid',
                    value: registration.messaging_service_sid,
                },
            ],
        },
        {
            title: '5. Campaign',
            fields: [
                { label: 'campaign_id', value: registration.campaign_id },
                {
                    label: 'campaign_approval_status',
                    value: registration.campaign_approval_status,
                },
            ],
        },
    ];

    return (
        <div className="mt-3 space-y-4">
            {registration.last_error && (
                <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                    <span className="font-medium">Last Error:</span>{' '}
                    {registration.last_error}
                </div>
            )}
            {sections.map((section) => (
                <div key={section.title}>
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                        {section.title}
                    </div>
                    <div className="grid gap-1">
                        {section.fields.map((field) => (
                            <div
                                key={field.label}
                                className="flex items-center gap-2 text-xs"
                            >
                                <span className="text-muted-foreground w-80 shrink-0 truncate font-mono">
                                    {field.label}
                                </span>
                                {field.isStatus ? (
                                    <ApprovalStatusBadge status={field.value} />
                                ) : (
                                    <span
                                        className={cn(
                                            'truncate font-mono',
                                            field.value
                                                ? 'text-foreground'
                                                : 'text-muted-foreground',
                                        )}
                                    >
                                        {field.value ?? '—'}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ApprovalStatusBadge({
    status,
}: {
    status: string | null | undefined;
}) {
    if (!status) {
        return <span className="text-muted-foreground text-xs">—</span>;
    }
    const variant =
        status === 'approved'
            ? 'default'
            : status === 'rejected'
              ? 'destructive'
              : 'secondary';
    return (
        <Badge variant={variant} className="text-xs">
            {status}
        </Badge>
    );
}
